// =============================================================================
// CodingCard — T-331. One slim, self-contained AP coding card.
// Used singly (manual new invoice) and stacked (the receipts list, bank-rec
// style). AI pre-fills from the pending receipt; blanks where unsure; you fill
// the rest and Approve. High-confidence cards are tinted green by the parent.
// Lookups (suppliers/projects/budget codes/cards/brands) are passed in so a
// list of N cards doesn't fire N×5 queries.
// =============================================================================

import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import BudgetPoPanel from "./BudgetPoPanel";   // 28 Oct 2026 — PO + bucket with what is left
import { splitFromInc } from "@/lib/gst";   // T-403
import { useRequireAuth } from "@/lib/auth";
import { Button, Select } from "@/components/ui";
import { FileText, Upload, CheckCircle2, AlertTriangle, Ban, ChevronRight, ChevronDown } from "lucide-react";
import { isPresenting, MONEY_MASK } from "@/lib/presenting";

// T-485 — the five Xero tax types AP can post under, and the ones that carry
// no GST at all (so the 1/11th split must not run on them).
const TAX_TYPES = ["GST on Expenses", "GST Free Expenses", "BAS Excluded", "GST on Income", "GST Free Income"];
const NO_GST_TAX_TYPES = ["GST Free Expenses", "GST Free Income", "BAS Excluded"];

// T-489 — a receipt line as the card holds it. Amounts stay strings so a
// half-typed "12." doesn't get rounded out from under the cursor.
interface ApLine {
  key: string;               // render key only; the DB numbers the lines itself
  description: string;
  quantity: string;
  unit_price: string;
  amount_inc: string;        // what is printed on the receipt — the only figure typed
  amount_ex_gst: string;     // derived from amount_inc + the tax type
  gst_amount: string;        // derived
  xero_account_code: string;
  xero_tax_type: string;
  is_collapsed: boolean;
  collapsed_count: number;
}

// T-489 — below this a line isn't worth its own row in Xero.
const SUNDRY_UNDER = 50;

let lineSeq = 0;
function blankLine(patch: Partial<ApLine> = {}): ApLine {
  return {
    key: `l${++lineSeq}`, description: "", quantity: "", unit_price: "",
    amount_inc: "", amount_ex_gst: "", gst_amount: "", xero_account_code: "", xero_tax_type: "",
    is_collapsed: false, collapsed_count: 0, ...patch,
  };
}
// 23 Sep 2026 — a date off the document, or blank. Viva Energy's statement
// prints "Due Now" in its due-date box; the extractor returned it verbatim, the
// date input showed it as blank (invalid values render empty), the card still
// treated it as "has a due date" and auto-picked Bill, and approving died in
// promote_pending_receipt on ('Due Now')::date. ISO stays, d/m/yyyy converts,
// any phrase becomes "" — and the phrase is shown under the field instead.
function isoDateOrBlank(v: unknown): string {
  const raw = String(v ?? "").trim();
  if (!raw) return "";
  let m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const mo = Number(m[2]), d = Number(m[3]);
    return (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) ? `${m[1]}-${m[2]}-${m[3]}` : "";
  }
  m = raw.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (m) {
    const d = Number(m[1]), mo = Number(m[2]);
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return `${m[3]}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }
  return "";
}
// What the document said where a date should be, when it wasn't one.
function dateTextOf(ai: any, field: "due_date" | "invoice_date"): string | null {
  const phrase = String(ai?.[`${field}_text`] ?? "").trim();
  if (phrase) return phrase;
  const raw = String(ai?.[field] ?? "").trim();
  return raw && !isoDateOrBlank(raw) ? raw : null;
}

// T-508 — Presenting mode.
const money = (n: number) => isPresenting() ? MONEY_MASK
  : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// T-489 — SPLIT BY ACCOUNT, COLLAPSE WITHIN ACCOUNT. Lines that would hit
// different Xero accounts must never merge — that split is the entire point of
// having lines at all. Inside one account, immaterial lines roll into one
// summary line, so a Bunnings docket becomes "$900 impact driver" plus
// "Sundry consumables (7 items)" instead of nine rows nobody will read.
function collapseExtractedLines(items: any[]): ApLine[] {
  const num = (v: any) => Number(v) || 0;
  // T-486 — the printed price is what we want, and the extractor now returns it
  // directly. Older reads only carry ex-GST, so fall back to grossing that up.
  const incOf = (it: any) => {
    const inc = num(it?.amount_inc_gst);
    if (inc) return inc;
    const ex = num(it?.amount_ex_gst), gst = num(it?.gst);
    if (ex && gst) return ex + gst;
    return ex ? ex * 1.1 : 0;
  };
  // Group by the Xero ACCOUNT, not by the model's loose category — the whole
  // rule is never to merge two things that post to different codes. Where the
  // extractor gave no account we still group by category so a docket without
  // coding doesn't come through as twenty separate rows.
  // BK-312 - the tax treatment is part of the key: a GST-free line (a rego, a
  // levy, a bank fee) must never collapse into a GST-bearing sundry pile, or
  // its treatment is silently lost. Same rule as accounts, for the same reason.
  const keyOf = (it: any) =>
    (String(it?.xero_account_code ?? "").trim()
      || `cat:${String(it?.suggested_category ?? "").trim() || "items"}`)
    + `|tax:${String(it?.suggested_tax_type ?? "").trim()}`;

  const groups: { key: string; label: string; account: string; big: any[]; small: any[] }[] = [];
  for (const it of Array.isArray(items) ? items : []) {
    const key = keyOf(it);
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = {
        key,
        label: String(it?.suggested_category ?? "").trim() || "items",
        account: String(it?.xero_account_code ?? "").trim(),
        big: [], small: [],
      };
      groups.push(g);
    }
    (incOf(it) < SUNDRY_UNDER ? g.small : g.big).push(it);
  }

  const out: ApLine[] = [];
  const asLine = (it: any, g: any) => blankLine({
    description: String(it?.description ?? "").trim() || g.label,
    quantity: it?.quantity != null ? String(it.quantity) : "",
    unit_price: it?.unit_price_ex_gst != null ? String(it.unit_price_ex_gst) : "",
    amount_inc: incOf(it).toFixed(2),
    xero_account_code: g.account,
    // What the model read off the document for THIS line. The document is the
    // only legitimate source of a tax treatment (BK-312 2) - account defaults
    // are banned from the chain outright.
    xero_tax_type: String(it?.suggested_tax_type ?? "").trim(),
  });

  for (const g of groups) {
    for (const it of g.big) out.push(asLine(it, g));
    // A lone small line is just a line — only relabel it as sundry when there
    // is actually more than one thing being folded together.
    if (g.small.length === 1) {
      out.push(asLine(g.small[0], g));
    } else if (g.small.length > 1) {
      const n = g.small.length;
      out.push(blankLine({
        description: `Sundry ${g.label} (${n} items)`,
        amount_inc: g.small.reduce((t, it) => t + incOf(it), 0).toFixed(2),
        xero_account_code: g.account,
        is_collapsed: true,
        collapsed_count: n,
      }));
    }
  }
  return out;
}

// T-489 — the Xero account box, lifted out of the coding section so a
// line row can use the same control. `compact` is the in-row variant; the full
// one is unchanged. It opens on an empty box, so a delayed blur is what closes it.
export function XeroAccountPicker({ value, onChange, accounts, compact, preferCode }: {
  value: string;
  onChange: (code: string) => void;
  accounts: any[];
  compact?: boolean;
  preferCode?: string | null;   // lead with the suggested account on an empty box
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const sel = accounts.find((a: any) => a.code === value) ?? null;
  // 64 accounts behind optgroups was unusable, so the picker searches:
  // code prefix, name or description ("fuel", "hire", "62"). ~64 rows, so it is
  // recomputed per render rather than memoised — which also lets the ordering
  // read the suggestion without a stale-deps dance.
  const q = search.trim().toLowerCase();
  const filtered = accounts
    .filter((a: any) => !q
      || String(a.code ?? "").toLowerCase().startsWith(q)
      || String(a.name ?? "").toLowerCase().includes(q)
      || String(a.description ?? "").toLowerCase().includes(q))
    // Empty box: lead with whatever suggest_ap_coding picked, so the common
    // case is one click. Everything else keeps the query's code order.
    .sort((a: any, b: any) => q ? 0
      : (b.code === preferCode ? 1 : 0) - (a.code === preferCode ? 1 : 0))
    .slice(0, 12);
  return (
    <div className="relative">
      <input value={sel ? `${sel.code} — ${sel.name}` : search}
        onChange={(e) => { onChange(""); setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        placeholder={compact ? "Account…" : "Xero account — search code, name or what it's for"}
        className={compact
          ? "w-full bg-ink border border-line rounded px-2 py-1 pr-6 text-[11px] text-text"
          : "w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"} />
      {sel && (
        <button type="button" title="Pick a different account" onMouseDown={(e) => e.preventDefault()}
          onClick={() => { onChange(""); setSearch(""); setOpen(true); }}
          className={`absolute right-2 top-1/2 -translate-y-1/2 ${compact ? "text-[11px]" : "text-[12px]"} text-text-muted hover:text-text`}>×</button>
      )}
      {open && filtered.length > 0 && (
        // The compact box is too narrow to read an account name in, so its list
        // is given a usable width of its own.
        <div className={`absolute z-20 mt-1 ${compact ? "w-72 right-0 max-h-44" : "w-full max-h-60"} bg-ink-card border border-line rounded-md shadow-lg overflow-y-auto`}>
          {filtered.map((a: any) => (
            <button key={a.code} type="button" onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(a.code); setSearch(""); setOpen(false); }}
              className={`w-full flex items-center gap-2 text-left px-3 py-1.5 hover:bg-ink-raised ${compact ? "text-[11px]" : "text-[12px]"}`}>
              <span className="truncate">{a.code} — {a.name}</span>
              <span className="ml-auto shrink-0 text-[11px] text-text-muted">{a.account_type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export interface ApLookups {
  suppliers: any[];
  projects: any[];
  budgetCodes: any[];   // { budget_code, description }
  cards: any[];         // card_register
  brands: any[];        // brand_map { brand_id, brand_display_name }
}

interface Props {
  receipt?: any;        // pending_receipts row incl. ocr_raw_json (+ auto_coding)
  imageUrl?: string | null;
  lookups: ApLookups;
  onDone: () => void;
}

export default function CodingCard({ receipt, imageUrl, lookups, onDone }: Props) {
  const user = useRequireAuth();
  const ai = (receipt?.ocr_raw_json ?? {}) as any;
  const auto = (ai.auto_coding ?? {}) as any;
  // T-487 — what the AI read off the paperwork, kept apart from what the
  // operator ends up picking: it is the input to resolution and the thing we
  // teach back as an alias afterwards.
  const aiSupplierName = String(ai.supplier_name ?? "").trim();
  const aiSupplierAbn = String(ai.supplier_abn ?? ai.abn ?? "").trim();

  const isPdf = receipt?.mime_type === "application/pdf";
  const matchCard = lookups.cards.find((c) => c.last4 === String(ai.card_last4 ?? "").replace(/\D/g, "").slice(-4));

  const [supplierId, setSupplierId] = useState("");   // 28 Oct — set from the matched PO below once the card has mounted (the name/ABN resolver may refine it)
  const [vendorName, setVendorName] = useState(ai.supplier_name ?? receipt?.supplier_guess ?? "");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [total, setTotal] = useState(ai.total_inc_gst != null ? String(ai.total_inc_gst) : (receipt?.amount_guess != null ? String(receipt.amount_guess) : ""));
  const [exGst, setExGst] = useState(ai.subtotal_ex_gst != null ? String(ai.subtotal_ex_gst) : "");
  const [gst, setGst] = useState(ai.gst_amount != null && Number(ai.gst_amount) > 0 ? String(ai.gst_amount) : "");
  // The GST box holds what you TYPE while you type (James, 28 Sep: "hard to
  // type in and mixes the number you put in") — it used to reformat to two
  // decimals on every keystroke. Committed on blur / Enter.
  const [gstDraft, setGstDraft] = useState<string | null>(null);
  // 28 Oct 2026 — our PO number on their invoice: the worker matched it to the register,
  // so the job, the PO and the bucket are set before anyone touches the card.
  const poMatch = (auto.po_match ?? null) as null | { po_id: string; po_number: string; po_title: string | null; project_id: string | null; supplier_id: string | null; budget_code: string | null; matched_on: string };
  const [invoiceType, setInvoiceType] = useState<"project" | "opex" | "capex">(receipt?.project_id || poMatch?.project_id ? "project" : "opex");
  // mig 351 — CapEx asset & insurance flag: configurable threshold + excluded
  // codes live in finance_settings, never hard-coded (James's ticket).
  // Mig 397 — the vehicle on the bill. James, 28 Sep: "add a Vehicle dropdown
  // to the AP coding card … assign a cost to a specific vehicle or a generic /
  // unallocated vehicle-cost bucket." The box appears when the bill is on a
  // vehicle Xero account (finance_settings.vehicle_xero_accounts) or a vehicle
  // budget code; blank = the fleet.
  const [vehicleId, setVehicleId] = useState<string>(receipt?.vehicle_id ?? "");
  // Mig 406 — litres on a fuel bill. James, 29 Sep: "receipts can capture
  // litres when it's fuel … the AP worker does put the litres in the
  // description." Pre-filled from the description the same way the DB
  // trigger reads it; blank for a Viva lump; typed over when the reader missed.
  const [fuelLitres, setFuelLitres] = useState<string>(receipt?.fuel_litres != null ? String(receipt.fuel_litres) : "");
  const litresFromText = (t: string | null | undefined): string => {
    const cleaned = String(t ?? "").replace(/[@$]\s*\$?\s*\d+(\.\d+)?\s*(\$\s*)?\/?\s*l(itres?|trs?)?\b/gi, " ").replace(/\$\s*\d+(\.\d+)?/g, " ");
    const m = cleaned.match(/(\d+(?:\.\d+)?)\s*(?:l|ltrs?|litres?)\b/i);
    return m ? String(Math.round(Number(m[1]) * 100) / 100) : "";
  };
  const { data: vehicleRule } = useQuery({
    queryKey: ["vehicle_rule"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [{ data: fs }, { data: vs }] = await Promise.all([
        (supabase as any).from("finance_settings").select("value").eq("key", "vehicle_xero_accounts").maybeSingle(),
        (supabase as any).from("vehicles").select("vehicle_id, vehicle_code, rego, make, model, is_active").eq("is_active", true).order("vehicle_code"),
      ]);
      return {
        accounts: String(fs?.value ?? "6200,450,449,451,453,730,731").split(",").map((x: string) => x.trim()).filter(Boolean),
        vehicles: (vs ?? []) as { vehicle_id: string; vehicle_code: string; rego: string | null; make: string | null; model: string | null }[],
      };
    },
  });
  // Mig 403 — the POLICY on the bill and HOW it is settled (BK-313 piece 3).
  // The picker appears when the bill is on an insurance Xero account
  // (finance_settings.insurance_xero_accounts) or coded OX06_INS. "Settled by"
  // appears with it and with the vehicle picker: a premium Elantis pays, or a
  // ute NAB pays the dealer for, is settled by the LOAN — nothing leaves the
  // bank for this bill, the loan schedule does that. Default bank.
  const [policyItemId, setPolicyItemId] = useState<string>(receipt?.policy_item_id ?? "");
  const [settledBy, setSettledBy] = useState<"bank" | "loan">(receipt?.settled_by === "loan" ? "loan" : "bank");
  const [settledLoanId, setSettledLoanId] = useState<string>(receipt?.settled_loan_id ?? "");
  const { data: insuranceRule } = useQuery({
    queryKey: ["insurance_rule"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [{ data: fs }, { data: ps }, { data: ls }] = await Promise.all([
        (supabase as any).from("finance_settings").select("value").eq("key", "insurance_xero_accounts").maybeSingle(),
        (supabase as any).from("company_compliance_items").select("item_id, display_name, category, provider, reference, expiry_date").eq("item_type", "insurance").eq("is_active", true).order("display_name"),
        (supabase as any).from("loans").select("loan_id, lender, title, facility_type, status").in("status", ["offer", "active", "restructured"]).order("lender"),
      ]);
      return {
        accounts: String(fs?.value ?? "").split(",").map((x: string) => x.trim()).filter(Boolean),
        policies: (ps ?? []) as { item_id: string; display_name: string; category: string | null; provider: string | null; reference: string | null; expiry_date: string | null }[],
        loans: (ls ?? []) as { loan_id: string; lender: string; title: string; facility_type: string; status: string }[],
      };
    },
  });
  const { data: assetRule } = useQuery({
    queryKey: ["asset_flag_rule"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await (supabase as any).from("finance_settings")
        .select("key, value").in("key", ["asset_insurance_threshold", "asset_flag_exclude_codes"]);
      const m = new Map<string, string>(((data ?? []) as any[]).map((r: any) => [r.key, r.value]));
      return {
        threshold: Number(m.get("asset_insurance_threshold") ?? 5000) || 5000,
        exclude: String(m.get("asset_flag_exclude_codes") ?? "CX01_VEHICLE")
          .split(",").map((x) => x.trim().toUpperCase()).filter(Boolean),
      };
    },
  });
  const [assetPrompt, setAssetPrompt] = useState<null | { value: number; apId: string | null }>(null);
  const [projectId, setProjectId] = useState(receipt?.project_id ?? poMatch?.project_id ?? "");
  const [budgetCode, setBudgetCode] = useState(auto.suggested_budget_code ?? "");
  const [ftcClass, setFtcClass] = useState(auto.suggested_ftc_class ?? "");
  const [manualFuel, setManualFuel] = useState(false);
  const [cardId, setCardId] = useState(auto.card?.card_id ?? matchCard?.card_id ?? "");
  const [brandId, setBrandId] = useState("");
  const [dueDate, setDueDate] = useState(isoDateOrBlank(ai.due_date));   // 23 Sep 2026 — never a phrase
  const [entryOverride, setEntryOverride] = useState<null | "bill" | "spend_money">(null);   // T-442 — Bill vs Spend, chosen at approval
  const [invoiceDate, setInvoiceDate] = useState(isoDateOrBlank(ai.invoice_date));
  const [invoiceNumber, setInvoiceNumber] = useState(ai.invoice_number ?? "");
  const [description, setDescription] = useState((ai.line_items?.[0]?.description as string) ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [poId, setPoId] = useState(poMatch?.po_id ?? "");   // T-380 — link to an open PO so its commitment converts; 28 Oct — pre-set from the PO number on the invoice
  // T-487 — supplier resolution. Invoice names rarely substring-match the
  // register ("HYDROEXCAVAC Civil Solutions Pty Ltd" vs "Hydro Excavac"), so the
  // RPC ranks candidates: ABN/alias/name are certain enough to apply, fuzzy only offers.
  const [resolvedVia, setResolvedVia] = useState<string | null>(null);
  const [resolvedSupplier, setResolvedSupplier] = useState<any>(null);
  const [fuzzySuppliers, setFuzzySuppliers] = useState<any[]>([]);
  const [resolveDone, setResolveDone] = useState(false);
  const resolveRan = useRef(false);
  // T-485 — Xero GL coding. The account supplies the tax default, but a tax the
  // coder picked themselves has to survive a later account change.
  // Seeded, not blank. Until 21 Aug 2026 both of these started empty and the
  // model's answer was thrown away: the document-level xero_account_code it
  // returns, and auto_coding.suggested_account from the brand defaults, were
  // read by nothing. Only PER-LINE codes survived, and only on a document that
  // collapsed to two or more priced lines - so a fuel docket, a one-line subbie
  // bill or any single-service invoice arrived with an empty picker however
  // well the model had coded it.
  //
  // The suggestion is still only a starting point. A learned correction
  // overrides it below, and the reviewer overrides everything.
  // Document first, brand default second - same order as the tax above, and for
  // the same reason.
  const [xeroAccountCode, setXeroAccountCode] = useState<string>(
    ai.xero_account_code ?? auto.suggested_account ?? "");
  // Tax seeds ONLY from what the model read off the document, never from the
  // brand default. coding_defaults.suggested_tax_rate has sat unread since it
  // was built, so it has never been corrected by use - promoting it to the
  // header tax would push years of unvalidated assumptions into the BAS. The
  // document is evidence; a brand default is a guess nobody has checked.
  // BK-312 2.4 - resolution order, no exceptions: (a) the document ->
  // (b) the supplier's GST registration (the worker resolves that into
  // auto.suggested_tax_rate; only a definite not-registered says anything) ->
  // (c) EMPTY, and the human picks before anything can be approved. The old
  // silent default of "GST on Expenses" was exactly the imputed-GST failure:
  // a $59 GST-free CPR course arrived showing $5.36 of GST that nobody charged.
  const [xeroTaxType, setXeroTaxType] = useState<string>(
    ai.suggested_tax_type ?? auto.suggested_tax_rate ?? "");
  const [taxTouched, setTaxTouched] = useState(false);
  const [codingHint, setCodingHint] = useState<string | null>(null);

  // Where a pre-filled account came from, when no learned correction has spoken
  // yet. An unexplained pre-filled box is worse than an empty one - people
  // either trust it blindly or distrust the whole screen.
  const seedHint = ai.xero_account_code
    ? "read off the document"
    : auto.suggested_account
      ? `suggested from ${auto.brand ?? "the supplier's usual coding"}`
      : null;

  // Codes the model returned that are not in the Xero chart. The worker deletes
  // them rather than guessing, which is right - but until now it said so only
  // into ocr_raw_json.review_flags, which no screen read. An empty account box
  // with no explanation is how a reviewer concludes the AI is useless.
  // The model already tells us when a document is a statement rather than a tax
  // invoice - document_kind, plus an explanation in its own notes. On the Viva
  // account statement it said so at 95% confidence: "This is a statement
  // summary, not a detailed tax invoice... would require the individual Shell
  // Card tax invoices themselves." Nothing displayed a word of it, so the
  // statement coded up as an ordinary bill.
  //
  // A closing balance equals an invoice amount only when last period happened to
  // be paid in full. And GST cannot be claimed from a document that says it is
  // not a tax invoice.
  const isStatement = String(ai.document_kind ?? "") === "statement";
  const kindNote = String(ai.confidence?.notes ?? "").trim();
  // 23 Sep 2026 — James, on a Viva fuel statement paid on the company card:
  // "I added the credit card surcharge and I don't want to split it into
  // separate bills, why would I want that here. One is clean and simple. It's
  // all fuel." The split is right for a supplier statement awaiting invoices
  // and wrong once the record is a card spend-money with its own lines. It is
  // still there behind one click, never the recommended action. (State must
  // sit above every early return — hooks rule.)
  const [showSplitAnyway, setShowSplitAnyway] = useState(false);

  const accountFlags: string[] = Array.isArray(ai.review_flags)
    ? (ai.review_flags as any[])
        .filter(f => f?.kind === "account_code" && f?.note)
        .map(f => String(f.note))
    : [];
  const suggested = useRef<{ account: string; tax: string } | null>(null);
  // Live mirror of the picks so the suggestion lookup can read them without
  // re-firing every time one of them changes.
  // accountTouched, not just "is the box full". The box now arrives pre-filled
  // from the model, and a pre-filled box must not block a learned correction
  // from improving on it - only a human pick does that.
  const [accountTouched, setAccountTouched] = useState(false);
  const live = useRef({ account: "", accountTouched: false, taxTouched: false, total: "" });
  live.current = { account: xeroAccountCode, accountTouched, taxTouched, total };
  // T-489 — the line split. Empty for the ordinary single-amount receipt; only
  // a multi-account docket ever fills it, and then the header total is its sum.
  const [lines, setLines] = useState<ApLine[]>([]);
  const [linesOpen, setLinesOpen] = useState(false);   // collapsed until asked for
  // G1 — how to read the amounts PRINTED on the document.
  //
  // A servo docket, a hotel bill, an Uber receipt all print GST-INCLUSIVE
  // prices, and that has been the assumption everywhere. Trade invoices very
  // often do the opposite: ex-GST line amounts with the GST added at the bottom.
  // GS & BD Electrics, 19 Aug: lines 990.00 + 49.65 + 196.00 = 1,235.65, invoice
  // total 1,359.22. Read as inclusive that derives $112.33 of GST; read as
  // exclusive it derives $123.57 and lands exactly on the invoice.
  //
  // Getting this wrong posts a wrong GST figure to Xero every time, so it is a
  // toggle rather than a guess. Input mode only - nothing new is stored, because
  // what gets saved has always been the DERIVED ex and GST figures.
  const [printedAs, setPrintedAs] = useState<"inc" | "ex">("inc");
  const seedRan = useRef(false);
  const qc = useQueryClient();

  // Quick-add supplier (James, Q4 cleanup): most one-off vendors should STAY
  // name-only (that's the design — no register clutter), but when a vendor is
  // a real ongoing supplier (Diggermate plant hire), promote them to the
  // register right here — prefilled from the invoice, no screen-hopping.
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [nsAbn, setNsAbn] = useState<string>(String(ai.supplier_abn ?? ai.abn ?? ""));
  const [nsEmail, setNsEmail] = useState("");
  const [nsSaving, setNsSaving] = useState(false);

  async function quickAddSupplier() {
    const name = vendorName.trim();
    if (!name) { window.alert("Type the supplier name first."); return; }
    setNsSaving(true);
    try {
      const base = name.toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 14) || "SUPPLIER";
      for (const code of [base, `${base}-2`, `${base}-${Date.now().toString(36).slice(-4).toUpperCase()}`]) {
        const { data, error } = await supabase.from("suppliers").insert({
          supplier_code: code,
          legal_name: name,
          abn: nsAbn.trim() || null,
          billing_email: nsEmail.trim() || null,
          is_active: true,
        }).select("supplier_id, legal_name").single();
        if (!error && data) {
          setSupplierId((data as any).supplier_id);
          setVendorName(""); setSearch(""); setOpen(false);
          setAddingSupplier(false);
          qc.invalidateQueries({ queryKey: ["ap_card_lookups"] });
          return;
        }
        if (error && !/duplicate|unique/i.test(error.message)) { window.alert(`Couldn't add supplier: ${error.message}`); return; }
      }
      window.alert("Couldn't generate a unique supplier code — add them via Suppliers › New.");
    } finally { setNsSaving(false); }
  }

  // T-380 — open POs (shared cache across all cards on the screen). Linking the
  // invoice to its PO is what nets the PO's committed spend down to actual.
  const { data: openPos = [] } = useQuery({
    queryKey: ["ap_open_pos"],
    queryFn: async () => {
      const { data } = await supabase
        .from("purchase_orders")
        .select("po_id, po_number, po_title, supplier_id, total_inc_gst, status")
        .in("status", ["sent", "acknowledged", "received", "invoiced"])
        .order("po_number", { ascending: false })
        .limit(50);
      return (data ?? []) as any[];
    },
  });
  // Prefer POs for the picked supplier; fall back to all open.
  const candidatePos = useMemo(() => {
    const matched = supplierId ? openPos.filter((p: any) => p.supplier_id === supplierId) : [];
    return matched.length > 0 ? matched : openPos;
  }, [openPos, supplierId]);

  // T-485 — the AP-usable slice of the Xero chart of accounts. Same list for
  // every card on the screen and it barely ever moves, so cache it hard.
  const { data: xeroAccounts = [] } = useQuery({
    queryKey: ["xero_accounts_ap"],
    staleTime: 6 * 60 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("xero_accounts")
        .select("code, name, account_type, tax_code, description")
        .eq("usable_for_ap", true)
        .eq("active", true)
        .order("code");
      return (data ?? []) as any[];
    },
  });
  // The seed effect runs on mount, before this query resolves, so it reads the
  // list through a ref rather than a stale closure.
  const xeroAccountsRef = useRef<any[]>([]);
  xeroAccountsRef.current = xeroAccounts;
  const selSupplier = lookups.suppliers.find((s) => s.supplier_id === supplierId)
    // T-487 — an ABN match can land on a supplier outside the cached active list;
    // without this the name box would blank out after auto-selecting.
    ?? (resolvedSupplier?.supplier_id === supplierId ? resolvedSupplier : undefined);
  // The coding memory is keyed on the supplier name as picked/typed, and on
  // whether this is project spend or overhead.
  const supplierName = (selSupplier?.legal_name ?? vendorName ?? "").trim();
  const codingContext = projectId ? "project" : "opex";
  const selCard = lookups.cards.find((c) => c.card_id === cardId) ?? null;
  // T-374b — the worker's own declaration at capture makes it a reimbursement
  // regardless of whether a personal card exists in the register.
  const claimedPersonal = !!receipt?.claimed_personal;
  const cardIsReimb = selCard?.card_type === "employee_personal" || (claimedPersonal && !selCard);
  const cardPaymentMethod = selCard
    ? (selCard.card_type === "company_credit" ? "card_credit" : selCard.card_type === "company_debit" ? "card_debit" : "reimbursement")
    : (claimedPersonal ? "reimbursement" : null);
  const reimburseEmployeeId = selCard?.card_type === "employee_personal"
    ? (selCard?.holder_employee_id ?? null)
    : (claimedPersonal ? (receipt?.captured_by_employee_id ?? null) : null);

  // T-442 — Bill vs Spend-money decided here at approval (was only set at Push-to-Xero).
  // Reimbursements + supplier bills with a due date => Bill; company-card spend => Spend money.
  const computedEntry: "bill" | "spend_money" =
    cardIsReimb ? "bill" : selCard ? "spend_money" : dueDate ? "bill" : "spend_money";
  // A reimbursement is a bill to the person, full stop - even a stale manual
  // override from before the personal card was picked can't flip it.
  const xeroEntryType = cardIsReimb ? "bill" : (entryOverride ?? computedEntry);

  const filteredSuppliers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return lookups.suppliers.filter((s) => s.legal_name?.toLowerCase().includes(q) || s.abn?.toLowerCase().includes(q)).slice(0, 8);
  }, [search, lookups.suppliers]);

  const filteredBudget = useMemo(() => {
    const pfx = invoiceType === "project" ? "PX" : invoiceType === "opex" ? "OX" : "CX";
    return lookups.budgetCodes.filter((b) => String(b.budget_code).toUpperCase().startsWith(pfx));
  }, [invoiceType, lookups.budgetCodes]);

  // T-485 — the tax type now drives the split (it replaced the GST-free tick):
  // ASIC fees, gov charges and BAS-excluded lines carry no GST, so taking the
  // 1/11th out of those would be wrong.
  const taxIsFree = NO_GST_TAX_TYPES.includes(xeroTaxType);
  // BK-312 2 - GST is READ, not derived. When the document states a GST figure
  // it is authoritative: ex = total - stated GST, which is the only arithmetic
  // that lands a mixed invoice (a 10% invoice with one GST-free rego line
  // states GST 550.92 on a 6,347.25 total; dividing by 11 invents 577.02).
  // The 1/11 split is the FALLBACK for documents that show no GST line, and
  // only for GST-bearing tax types. gstFromDoc marks whether the figure in the
  // GST box is still the document's own number.
  // James, 28 Sep (HVAC Hilux, $75,000 with GST $6,818.18 printed): the read
  // came back gst_amount = 0 and the card took ZERO as "what the document
  // prints" — ex 75,000, GST 0.00, and the coded line saying 6,818.18 was
  // ignored. A stated figure is authoritative when it is a figure; a read of
  // 0 on a GST-bearing document is a miss, not a statement — treat it as
  // "none shown" and let the 1/11 split (or the coded lines) speak.
  const [gstFromDoc, setGstFromDoc] = useState<boolean>(ai.gst_amount != null && Number(ai.gst_amount) > 0);
  function recompute(totalStr: string, gstStr: string, free: boolean) {
    const t = Number(totalStr) || 0;
    if (!t) { setExGst(""); setGst(gstStr); return; }
    if (free) { setExGst(t.toFixed(2)); setGst("0.00"); return; }
    const g = Number(gstStr);
    if (gstStr !== "" && Number.isFinite(g)) {
      setExGst((t - g).toFixed(2));
      setGst(g.toFixed(2));
      return;
    }
    const { ex, gst } = splitFromInc(t);   // T-403 — shared lib/gst split
    setExGst(ex.toFixed(2));
    setGst(gst.toFixed(2));
  }
  function splitTotal(v: string, free: boolean = taxIsFree) {
    setTotal(v);
    recompute(v, gstFromDoc ? gst : "", free);
  }
  function editStatedGst(v: string) {
    // Typing here asserts "this is what the document prints" - it stays
    // authoritative until cleared. A blank or a zero is "none shown".
    const n = Number(v);
    const stated = v.trim() !== "" && Number.isFinite(n) && n > 0;
    setGstFromDoc(stated);
    setGst(stated ? v : "");
    recompute(total, stated ? v : "", taxIsFree);
  }
  function pickTaxType(t: string) {
    setTaxTouched(true);   // a deliberate pick outranks every suggestion from here on
    setXeroTaxType(t);
    recompute(total, gstFromDoc ? gst : "", NO_GST_TAX_TYPES.includes(t));
    // James, 26 Aug (CCT trailer, live): lines with no tax of their own DISPLAY
    // the header's tax but were still deriving off their old (possibly blank)
    // value - $0.00 GST behind a dropdown reading "GST 10%". A line that
    // follows the header must re-derive the moment the header moves, so what
    // the row shows is always what the maths used.
    setLines((prev) => prev.map((l) =>
      l.xero_tax_type ? l : { ...l, ...splitIncWith(l.amount_inc, t, printedAs) }));
  }
  function pickXeroAccount(code: string) {
    setXeroAccountCode(code);
    setAccountTouched(true);   // a human pick outranks every suggestion from here
    if (!code) return;   // clearing the box is the start of a pick, not one
    setCodingHint(null);   // their pick now, not the suggestion's
    // BK-312 2.4 - the account's default tax rate is NEVER an input to the tax
    // treatment. This used to adopt acct.tax_code, which is how account 313's
    // BAS Excluded default claimed $356.36 of GST that was never there, and how
    // 6012's GST default invented $5.36 on a GST-free course. The tax now comes
    // from the document, the supplier's registration, or the human - only.
  }

  // T-489 — where lines exist they are the truth: the header total is their sum
  // and the box up top goes read-only. Lines only ever exist at 2+ (see below),
  // so nothing here fires for the ordinary one-amount receipt.
  // T-502 — ONE derivation of what will post, used by the readout and by the
  // save, so the card can never display a GST different from the one it writes.
  // Amounts are parsed leniently: a pasted "$3,788.40" must not become NaN.
  function money2(v: string | number): number {
    const n = typeof v === "number" ? v : Number(String(v ?? "").replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) ? +n.toFixed(2) : 0;
  }
  function postingFigures(totalStr: string | number, exStr: string | number, free: boolean, statedGst?: string) {
    const t = money2(totalStr);
    // BK-312 2.1 - a stated GST figure is authoritative; the 1/11 path is only
    // for documents that show no GST line at all.
    if (!free && t && statedGst != null && statedGst !== "" && Number.isFinite(Number(statedGst))) {
      const g = money2(statedGst);
      return { total: t, ex: +(t - g).toFixed(2), gst: g };
    }
    let ex = free ? t : (money2(exStr) || (t ? +(t / 1.1).toFixed(2) : 0));
    let g  = free ? 0 : +(t - ex).toFixed(2);
    // Snap a rounding-sized gap to the ATO 1/11 rule (see T-500b).
    if (!free && t) {
      const ato = +(t / 11).toFixed(2);
      if (Math.abs(g - ato) <= 0.02) { g = ato; ex = +(t - g).toFixed(2); }
    }
    return { total: t, ex, gst: g };
  }
  const hasLines = lines.length > 0;
  const linesEx = lines.reduce((t, l) => t + (Number(l.amount_ex_gst) || 0), 0);
  const linesGst = lines.reduce((t, l) => t + (Number(l.gst_amount) || 0), 0);
  const linesTotal = +(linesEx + linesGst).toFixed(2);

  // James, 26 Aug (Josh's Uber reimbursement): the receipt printed NO GST
  // line, the header fell back to 1/11, James correctly marked the card
  // surcharge and reservation fee BAS Excluded on the lines - and the card
  // then scolded him for disagreeing with its own guess. When the document
  // states no GST figure, the coded lines are the only real information:
  // if they reconcile with the total, THEY set the posting GST. A stated
  // figure still wins over everything (BK-312 2.1), and with no usable lines
  // the 1/11 fallback stands.
  const posting = (() => {
    const base = postingFigures(total, exGst, taxIsFree, gstFromDoc ? gst : undefined);
    // 28 Sep: one coded line that reconciles is as good as two — the Hilux
    // invoice is a single line with its own GST and it must set the posting.
    if (!gstFromDoc && !taxIsFree && lines.length >= 1 && base.total
        && Math.abs(linesTotal - base.total) <= 0.05) {
      const g = +linesGst.toFixed(2);
      return { total: base.total, ex: +(base.total - g).toFixed(2), gst: g };
    }
    return base;
  })();
  const headerEx  = posting.ex.toFixed(2);
  const headerGst = posting.gst.toFixed(2);
  // T-489 — if the lines don't add up to what the extractor read off the
  // document, something got missed: a second page, a freight line, a card
  // surcharge. Say so rather than quietly posting a total that disagrees with
  // the paper the auditor will be holding.
  const lineVariance = (() => {
    if (lines.length < 2) return null;
    const stated = Number(total) || 0;
    if (!stated) return null;
    const diff = +(linesTotal - stated).toFixed(2);
    return Math.abs(diff) > 0.05 ? diff : null;
  })();

  // T-489 — the invoice prints its own GST total. Derived GST should equal it.
  // When it doesn't, either an account is on the wrong tax treatment or the
  // document has something in it we haven't accounted for. Either way it is a
  // question for a human, and it is a far better check than trusting the read.
  const gstCheck = (() => {
    if (lines.length < 2) return null;
    // Only a figure the DOCUMENT printed can be contradicted. Without one the
    // header GST is our own 1/11 fallback, and the coded lines outrank a
    // guess - see the posting derivation above.
    if (!gstFromDoc) return null;
    const stated = Number(gst) || 0;
    if (!stated) return null;
    const diff = +(linesGst - stated).toFixed(2);
    return Math.abs(diff) > 0.05 ? { diff, stated } : null;
  })();

  // T-AP-GST-GUARD — the Insurance Advisernet case (inv 604119073): a one-line
  // $565 "GST on Expenses" post made Xero derive $51.36 where the document
  // printed $47.03, because $47.62 of stamp duty inside the total is BAS
  // Excluded. The T-489 gstCheck above only runs once lines exist; this guard
  // covers the UN-SPLIT document — predict what Xero will derive (1/11 of the
  // inclusive total, or zero on a tax-free type) and compare it to the printed
  // figure BEFORE anything posts. Never auto-applies; the one-click split is
  // offered, the human decides.
  const xeroGuard = (() => {
    if (!gstFromDoc || lines.length > 0) return null;   // split docs are gstCheck's job
    const stated = Number(gst) || 0;
    const t = Number(total) || 0;
    if (!t) return null;
    const predicted = taxIsFree ? 0 : +(t / 11).toFixed(2);
    const diff = +(predicted - stated).toFixed(2);
    if (Math.abs(diff) <= 0.02) return null;
    // Moving $M off the GST line onto BAS Excluded lowers Xero's figure by M/11,
    // so M = 11 × the overshoot. On the insurance case: 11 × 4.33 = 47.63 ≈ the
    // printed stamp duty. Only offered when it makes sense (overshoot, M < total).
    const basSplit = diff > 0 ? +(diff * 11).toFixed(2) : null;
    return { predicted, stated, diff, basSplit: basSplit != null && basSplit < t ? basSplit : null };
  })();

  function applyBasSplit(M: number) {
    const t = Number(total) || 0;
    const main = +(t - M).toFixed(2);
    const mainTax = xeroTaxType || "GST on Expenses";
    setLines([
      blankLine({
        description: "Premium / charges (GST)",
        amount_inc: main.toFixed(2),
        ...splitIncWith(main, mainTax, "inc"),
        xero_account_code: xeroAccountCode, xero_tax_type: mainTax,
      }),
      blankLine({
        description: "Stamp duty / levies — BAS Excluded",
        amount_inc: M.toFixed(2),
        ...splitIncWith(M, "BAS Excluded", "inc"),
        xero_account_code: xeroAccountCode, xero_tax_type: "BAS Excluded",
      }),
    ]);
    setLinesOpen(true);
  }

  // BK-312 2.4 - a line with no tax of its own inherits the HEADER's, never
  // the account's default (which is banned from the chain - see pickXeroAccount).
  function taxForAccount(_code: string) {
    return xeroTaxType;
  }
  // T-489 — GST is DERIVED, never read off the document and never typed. The
  // line's tax type decides it: GST-bearing means a tenth of the ex-GST amount,
  // anything free or excluded means zero. The invoice's own GST total is used
  // as a CHECK further down, not as the source — that way a bad OCR read shows
  // up as a discrepancy instead of quietly becoming the number we post.
  // Receipts print tax-INCLUSIVE prices. Asking for ex-GST made the operator
  // divide by 1.1 in their head on every line, which is both slow and the exact
  // arithmetic the machine is here to do. So the typed figure is the printed one
  // and ex/GST fall out of it, same rule the header total already runs on.
  function splitIncWith(inc: string | number, taxType: string, mode: "inc" | "ex") {
    const n = Number(inc) || 0;
    if (!taxType || NO_GST_TAX_TYPES.includes(taxType)) {
      return { amount_ex_gst: n.toFixed(2), gst_amount: "0.00" };
    }
    // G1 — the printed figure is either inclusive or exclusive.
    if (mode === "ex") {
      const gstOnEx = Math.round(n * 10) / 100;          // 10% of the ex amount
      return { amount_ex_gst: n.toFixed(2), gst_amount: gstOnEx.toFixed(2) };
    }
    const ex = Math.round((n / 1.1) * 100) / 100;
    return { amount_ex_gst: ex.toFixed(2), gst_amount: (n - ex).toFixed(2) };
  }
  function splitInc(inc: string | number, taxType: string) {
    return splitIncWith(inc, taxType, printedAs);
  }
  // Flipping the mode re-derives every line from the figure already typed. The
  // typed number does not change - it is what the document says - only what we
  // take it to mean.
  function setPrintedMode(mode: "inc" | "ex") {
    setPrintedAs(mode);
    setLines((prev) => prev.map((l) => {
      const n = Number(l.amount_inc) || 0;
      const taxType = l.xero_tax_type || xeroTaxType;
      if (!taxType || NO_GST_TAX_TYPES.includes(taxType)) {
        return { ...l, amount_ex_gst: n.toFixed(2), gst_amount: "0.00" };
      }
      if (mode === "ex") {
        return { ...l, amount_ex_gst: n.toFixed(2), gst_amount: (Math.round(n * 10) / 100).toFixed(2) };
      }
      const ex = Math.round((n / 1.1) * 100) / 100;
      return { ...l, amount_ex_gst: ex.toFixed(2), gst_amount: (n - ex).toFixed(2) };
    }));
  }
  function patchLine(key: string, patch: Partial<ApLine>) {
    setLines((prev) => prev.map((l) => {
      if (l.key !== key) return l;
      const next = { ...l, ...patch };
      if (patch.amount_inc !== undefined || patch.xero_tax_type !== undefined) {
        Object.assign(next, splitInc(next.amount_inc, next.xero_tax_type || xeroTaxType));
      }
      return next;
    }));
  }
  function pickLineAccount(key: string, code: string) {
    patchLine(key, { xero_account_code: code, ...(code ? { xero_tax_type: taxForAccount(code) } : {}) });
  }
  // ------------------------------------------------------------------------
  // Start a split on a document that arrived as one amount.
  //
  // James, 21 Aug, on a single-line Viva fuel invoice: "Need an option to add
  // the lines if the worker misses or just puts a single line in. This one I
  // want to add a credit card fee at the end - I want to add that as a BAS
  // Excluded bank fee line."
  //
  // The lines table is deliberately absent on a one-amount docket - most of
  // them are one amount and must stay frictionless. But absent is not the same
  // as unavailable, and until now there was no way in at all: "+ Add line"
  // lived inside the table, so a document with no lines could never grow one.
  //
  // Seeds ONE line carrying the whole invoice at the current coding, then
  // "+ Add line" behaves as it always has. Splitting the fee off is: add a
  // line, type the fee, drop the first line by the same amount. The variance
  // strip says when it reconciles.
  // ------------------------------------------------------------------------
  function startSplit() {
    const inc = Number(total) || 0;
    setLines([blankLine({
      description: description || (ai.line_items?.[0]?.description as string) || "",
      amount_inc: inc ? String(inc) : "",
      xero_account_code: xeroAccountCode,
      xero_tax_type: xeroTaxType,
      ...splitInc(inc, xeroTaxType),
    })]);
    setLinesOpen(true);
  }
  function addLine() {
    setLines((prev) => [...prev, blankLine({ xero_account_code: xeroAccountCode, xero_tax_type: taxForAccount(xeroAccountCode) })]);
  }
  function removeLine(key: string) {
    const next = lines.filter((l) => l.key !== key);
    // One line is the same thing as no lines (that's the seeding rule), so a
    // delete down to one drops the split and hands the total back — editable,
    // at whatever the remaining lines add up to.
    if (next.length <= 1) {
      // T-502 — dropping the split must NOT touch the invoice total. This used
      // to overwrite the header with the sum of whatever lines were left, so
      // deleting down to one unpriced line set the total to $0.00 and the card
      // then failed to save at all. The total is the supplier's number; only the
      // split is ours to discard.
      setLines([]);
      setLinesOpen(false);
      return;
    }
    setLines(next);
  }

  // T-489 — seed from the extraction once, on mount. 0 or 1 line after
  // collapsing means there was nothing worth splitting: that invoice stays a
  // single-amount header, which is most of them, and must stay frictionless.
  useEffect(() => {
    if (seedRan.current) return;
    seedRan.current = true;
    if (poMatch?.supplier_id) setSupplierId(prev => prev || poMatch.supplier_id!);
    const rawSeeded = collapseExtractedLines(ai.line_items)
      // T-502 — a line with no price is not a split, it is a scope description.
      // Unique Energy's invoice lists three items under SCOPE OF WORKS and prices
      // none of them; only the footer carries ex/GST/total. Seeding those as
      // $0.00 lines made the card show ex 0.00 / GST 0.00 against a $3,788.40
      // invoice and left nothing that could be approved.
      .filter((l) => (Number(l.amount_inc) || 0) !== 0);
    // G1 auto-detect (James, 26 Aug - the CCT trailer and GS & BD invoices
    // both print EX-GST line amounts, and both arrived on the "inclusive"
    // default, so the lines never reconciled). The document itself settles it:
    // when the printed lines sum to (total - stated GST), they are exclusive;
    // when they sum to the total, inclusive. Only flips on a clean match -
    // anything ambiguous keeps the receipts-normally-print-inclusive default,
    // and the toggle still lets a human overrule.
    const rawSum = rawSeeded.reduce((t, l) => t + (Number(l.amount_inc) || 0), 0);
    const docTotal = Number(ai.total_inc_gst) || 0;
    const docGst = Number(ai.gst_amount);
    let seedMode: "inc" | "ex" = "inc";
    if (rawSeeded.length > 1 && docTotal && Number.isFinite(docGst) && docGst > 0
        && Math.abs(rawSum - (docTotal - docGst)) <= 0.05
        && Math.abs(rawSum - docTotal) > 0.05) {
      seedMode = "ex";
      setPrintedAs("ex");
    }
    const seeded = rawSeeded.map((l) => {
      // BK-312 2.4 - the line's tax is what the MODEL READ for that line
      // (carried in from collapseExtractedLines); a line with none follows the
      // header. The old seed asked the account's default tax here, which is
      // banned from the chain - and at mount the accounts list hadn't even
      // loaded, so it silently seeded blank.
      const tax = l.xero_tax_type || xeroTaxType;
      return { ...l, ...splitIncWith(l.amount_inc, tax, seedMode) };
    });
    if (seeded.length > 1) {
      setLines(seeded);
      // T-489 — the header description is what a single-line Xero entry carries,
      // and what shows as the narration when the lines go across. Leaving it as
      // line one's description reads as though the other five aren't there. Only
      // replaces the auto-filled value; a typed description is left alone.
      setDescription((cur) => {
        const auto = (ai.line_items?.[0]?.description as string) ?? "";
        if (cur.trim() && cur.trim() !== auto.trim()) return cur;
        const names = seeded.map((l) => l.description).filter(Boolean);
        const head = names.slice(0, 2).join(", ");
        const rest = names.length - 2;
        return `${seeded.length} lines — ${head}${rest > 0 ? ` + ${rest} more` : ""}`;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The header account arrives late (suggest_ap_coding is a round trip), so
  // lines inherit it by backfill rather than at seed time. Only ever fills
  // blanks — a line coded by hand is never overwritten.
  useEffect(() => {
    if (!xeroAccountCode) return;
    setLines((prev) => prev.some((l) => !l.xero_account_code)
      ? prev.map((l) => (l.xero_account_code ? l
          : { ...l, xero_account_code: xeroAccountCode,
                xero_tax_type: taxForAccount(xeroAccountCode),
                ...splitInc(l.amount_inc, taxForAccount(xeroAccountCode)) }))
      : prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xeroAccountCode, xeroTaxType, xeroAccounts]);

  // Ask the DB how this supplier + budget code was coded last time. Keyed on
  // supplier/budget/context only — the live ref keeps the rest off the deps.
  useEffect(() => {
    if (!supplierName || !budgetCode) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.schema("app").rpc("suggest_ap_coding", {
        p_supplier_name: supplierName,
        p_budget_code: budgetCode,
        p_context: codingContext,
      });
      if (cancelled || error) return;
      const sug = ((data ?? []) as any[])[0] ?? null;
      suggested.current = sug ? { account: sug.xero_account_code, tax: sug.xero_tax_type } : null;
      if (!sug) { setCodingHint(null); return; }
      // A learned correction outranks the model's read of one document - it is
      // the accumulated result of this being corrected before. It still never
      // overrides a human who has picked on THIS card.
      if (live.current.accountTouched) return;
      // Only claim a source once the suggestion is actually what's in the box.
      setCodingHint(sug.source === "budget_code" ? "from the budget code mapping" : "learned from your last correction");
      setXeroAccountCode(sug.xero_account_code);
      if (!live.current.taxTouched && sug.xero_tax_type) {
        setXeroTaxType(sug.xero_tax_type);
        splitTotal(live.current.total, NO_GST_TAX_TYPES.includes(sug.xero_tax_type));
      }
    })();
    return () => { cancelled = true; };
  }, [supplierName, budgetCode, codingContext]);

  // T-487 — resolve once, on mount. Anything the operator types afterwards is
  // them overruling us, and re-running would fight their typing.
  useEffect(() => {
    if (resolveRan.current) return;
    resolveRan.current = true;
    if (supplierId || !aiSupplierName) { setResolveDone(true); return; }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.schema("app").rpc("resolve_supplier", {
        p_name: aiSupplierName,
        p_abn: aiSupplierAbn || null,
      });
      if (cancelled) return;
      const rows = (error ? [] : ((data ?? []) as any[]));
      const top = rows[0] ?? null;
      if (top && top.matched_via !== "fuzzy") {
        setResolvedSupplier(top);
        setResolvedVia(top.matched_via);
        setSupplierId(top.supplier_id);
        setVendorName(""); setSearch("");
      } else if (top) {
        // Close, not certain — these get offered, never applied.
        setFuzzySuppliers(rows.filter((r) => r.matched_via === "fuzzy").slice(0, 3));
      }
      setResolveDone(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Undo an automatic match: hand the invoice's own name back to the free-text box.
  function unresolveSupplier() {
    setSupplierId(""); setResolvedVia(null); setResolvedSupplier(null);
    setVendorName(aiSupplierName); setSearch(""); setOpen(false);
  }
  function pickFuzzySupplier(s: any) {
    setSupplierId(s.supplier_id); setResolvedSupplier(s); setResolvedVia(null);
    setVendorName(""); setSearch(""); setOpen(false);
  }

  async function applyBrand(id: string) {
    setBrandId(id);
    if (!id) return;
    const brand = lookups.brands.find((b) => b.brand_id === id);
    if (brand && !supplierId && !vendorName.trim()) setVendorName(brand.brand_display_name);
    const { data } = await supabase.from("coding_defaults").select("suggested_budget_code, suggested_ftc_class").eq("brand_id", id).maybeSingle();
    if ((data as any)?.suggested_budget_code) setBudgetCode((data as any).suggested_budget_code);
    if ((data as any)?.suggested_ftc_class) setFtcClass((data as any).suggested_ftc_class);
  }

  function openImage() { if (imageUrl) window.open(imageUrl, "_blank", "noopener"); }

  async function reject() {
    if (!receipt) return;
    if (!window.confirm("Reject this receipt? It will drop off the list.")) return;
    setBusy(true); setErr(null);
    try {
      await supabase.from("pending_receipts").update({ status: "rejected" }).eq("receipt_id", receipt.receipt_id);
      onDone();
    } catch (e: any) { setErr(e?.message ?? "Couldn't reject"); } finally { setBusy(false); }
  }

  async function approve() {
    setBusy(true); setErr(null);
    try {
      // BK-312 1.4 proposed a hard block on approving statements. James, 26 Aug:
      // "Remove this block - I need flexibility. Xero is the final source of
      // truth and I reconcile those lines with totals... we need to load
      // statements and others to get them into the Bill to be paid." So the
      // split is the OFFERED path (and the right one when the statement spans
      // invoices), but approving the document as one record is his call. What
      // stays hard is the GST discipline: nothing derives GST from the balance.
      // BK-312 1.3 / 4.4 - no tax invoice behind the amount: no approval until
      // the evidence is attached and the flag cleared (button in the banner).
      if (receipt?.no_tax_invoice) throw new Error("No tax invoice supports this amount. Attach the underlying invoice (capture it and clear the flag) before approving — GST can never be claimed without one.");
      if (!supplierId && !vendorName.trim() && !brandId) throw new Error("Pick a supplier, vendor, or brand.");
      if (!budgetCode) throw new Error("Pick a budget code.");
      if (!xeroAccountCode) throw new Error("Pick a Xero account.");
      // BK-312 2.4(c) - when the document didn't state a treatment and the
      // supplier's registration doesn't settle it, a human decides. Explicitly.
      if (!xeroTaxType) throw new Error("Pick the tax treatment — the document doesn't state one, so this is your call, not a default's.");
      if (lines.some((l) => !l.xero_tax_type && !xeroTaxType)) throw new Error("Pick a tax treatment for every line.");
      // BK-312 2.2 - a derived GST that disagrees with the document's stated
      // GST is a hard stop, not a note. One of them is wrong; find out which.
      if (gstCheck != null) throw new Error(`GST mismatch — the document states ${gstCheck.stated.toFixed(2)} but the lines derive ${linesGst.toFixed(2)} (out by ${Math.abs(gstCheck.diff).toFixed(2)}). Fix the line tax treatments or correct the stated GST; don't approve a number the paperwork disagrees with.`);
      // BK-312 3/4 - capital and premises are overhead, never a project line
      // (the job picks capital up through depreciation). The DB enforces this
      // too; failing here just says it in words before the trigger does.
      if (projectId && (invoiceType === "capex" || budgetCode.toUpperCase().startsWith("CX"))) throw new Error("A capital purchase never carries a project — the job picks it up through depreciation. Set it to Capex overhead (no project).");
      if (projectId && xeroAccountCode === "469") throw new Error("Rent (469) is premises overhead — never a project line.");
      // Deliberately NOT a blocker. The total and the GST are what get recorded
      // and what Xero reconciles against; the line split is analysis on top. An
      // imperfect split should never stop a correct invoice being approved — it
      // is flagged on the card and in v_ap_coding_exceptions, and the Xero push
      // falls back to a single header line when the lines do not add up.
      if (lines.some((l) => !l.xero_account_code)) throw new Error("Pick a Xero account for every line.");
      // T-489 — with lines, they own the totals. The RPC re-sums the header off
      // them anyway; this just keeps the first write consistent with the second.
      // Lines allocate the invoice total; they never replace it.
      //
      // T-498 — ex used to come from the header while GST came from the SUM OF
      // THE LINES. Two different sources, so they only agreed by luck. On
      // Brendan Pevreal's bill the header said ex 3975.00 (right) and the lines
      // produced GST 361.36 — which is 3975/11, i.e. the ex-GST figure treated
      // as inclusive. Xero rejected it, correctly: 3975.00 + 361.36 is not
      // 4372.50.
      //
      // The rule James set stands: the invoice total is authoritative and the
      // lines are detail. So all three numbers are now derived from the header
      // and forced to reconcile — ex + GST always equals the total exactly.
      // T-502 - one derivation: what the readout shows is what gets written.
      const { total: totalNum, ex: exFinal, gst: gstNum } = posting;
      // T-502 — amount_inc_gst is NOT NULL on ap_invoices, and the promote RPC
      // writes whatever is in the receipt JSON. Sending an empty total surfaced
      // as a raw Postgres constraint error on the card. Refuse here, in words.
      if (!totalNum) {
        throw new Error("Enter the invoice total before approving — it's the figure everything else is worked out from.");
      }

      let apId: string | null = null;
      if (receipt) {
        // 23 Sep 2026 — promote_pending_receipt casts these with ::date, so they
        // are a real date or null here, never whatever the document printed.
        const updatedAi = { ...ai, supplier_name: vendorName.trim() || ai.supplier_name, invoice_number: invoiceNumber || null, invoice_date: isoDateOrBlank(invoiceDate) || null, due_date: isoDateOrBlank(dueDate) || null, total_inc_gst: totalNum, subtotal_ex_gst: exFinal, gst_amount: gstNum };
        await supabase.from("pending_receipts").update({ ocr_raw_json: updatedAi }).eq("receipt_id", receipt.receipt_id);
        const { data, error } = await supabase.schema("app").rpc("promote_pending_receipt", { p_receipt_id: receipt.receipt_id, p_budget_code: budgetCode, p_project_id: projectId || null });
        if (error) throw new Error(error.message);
        apId = (typeof data === "string" ? data : (data as any)?.ap_invoice_id) ?? null;
      } else {
        // (supabase as any) — vehicle_id (mig 397) not in database.types.ts yet
        const { data, error } = await (supabase as any).from("ap_invoices").insert({
          invoice_number: invoiceNumber || null,
          supplier_id: supplierId || null,
          supplier_name: selSupplier?.legal_name ?? (vendorName.trim() || null),
          brand_id: brandId || null,
          invoice_date: isoDateOrBlank(invoiceDate) || new Date().toISOString().slice(0, 10),
          due_date: isoDateOrBlank(dueDate) || null,
          amount_ex_gst: exFinal,
          gst_amount: gstNum,
          // The printed total, not a re-add of our own parts.
          amount_inc_gst: totalNum || +(exFinal + gstNum).toFixed(2),
          budget_code: budgetCode,
          project_id: projectId || null,
          captured_by: (user as any).user_id ?? null,
          capture_source: "manual",
          status: "captured",
          xero_entry_type: xeroEntryType,   // T-442
          xero_account_code: xeroAccountCode,   // T-485
          xero_tax_type: xeroTaxType,
          vehicle_id: vehicleId || null,   // mig 397
          fuel_litres: fuelLitres.trim() ? Number(fuelLitres) : null,   // mig 406
          policy_item_id: policyItemId || null,          // mig 403
          settled_by: settledBy,                          // mig 403
          settled_loan_id: settledBy === "loan" ? (settledLoanId || null) : null,
        }).select("ap_invoice_id").single();
        if (error) throw new Error(error.message);
        apId = (data as any)?.ap_invoice_id ?? null;
      }
      if (apId) {
        await (supabase as any).from("ap_invoices").update({
          // T-352 (coding-card parity): a supplier YOU picked/created wins over
          // the OCR's ABN/name match — James's ASIC "Registration" bug.
          ...(supplierId ? { supplier_id: supplierId, supplier_name: selSupplier?.legal_name ?? (vendorName.trim() || null) } : {}),
          po_id: poId || null,   // T-380 — converts the PO's committed → actual
          ftc_class: ftcClass || null,
          payment_method: cardPaymentMethod,
          card_id: cardId || null,
          is_reimbursement: cardIsReimb,
          reimburse_employee_id: reimburseEmployeeId,
          description: description.trim() || null,
          xero_entry_type: xeroEntryType,   // T-442 — Bill vs Spend chosen at approval
          xero_account_code: xeroAccountCode,   // T-485 — GL coding chosen at approval
          xero_tax_type: xeroTaxType,
          vehicle_id: vehicleId || null,   // mig 397 — the vehicle on the bill
          fuel_litres: fuelLitres.trim() ? Number(fuelLitres) : null,   // mig 406 — litres on a fuel bill
          policy_item_id: policyItemId || null,          // mig 403 — the policy on the bill
          settled_by: settledBy,                          // mig 403 — bank | loan
          settled_loan_id: settledBy === "loan" ? (settledLoanId || null) : null,
          // Carry the edited dates through (promote may not pick them up from the json).
          ...(isoDateOrBlank(invoiceDate) ? { invoice_date: isoDateOrBlank(invoiceDate) } : {}),
          due_date: isoDateOrBlank(dueDate) || null,
          // Carry the source receipt file onto the invoice so it shows in the AP queue.
          ...(receipt?.storage_path ? { pdf_storage_path: receipt.storage_path, pdf_original_filename: (String(receipt.storage_path).split("/").pop() ?? "receipt") } : {}),
        }).eq("ap_invoice_id", apId);
      }
      // T-485 — only teach the memory when we landed somewhere the suggestion
      // didn't. Never let it block: the coding is already saved on the invoice.
      const sug = suggested.current;
      if (supplierName && xeroAccountCode && (!sug || sug.account !== xeroAccountCode || sug.tax !== xeroTaxType)) {
        try {
          const { error: recErr } = await supabase.schema("app").rpc("record_ap_coding", {
            p_supplier_name: supplierName,
            p_budget_code: budgetCode,
            p_context: codingContext,
            p_xero_account_code: xeroAccountCode,
            p_xero_tax_type: xeroTaxType,
          });
          if (recErr) console.warn("record_ap_coding:", recErr.message);
        } catch (e: any) { console.warn("record_ap_coding:", e?.message ?? e); }
      }
      // T-487 — the register's name differed from the invoice's, so teach the
      // variant. Same rule as the coding memory: never let it block the approval.
      if (supplierId && aiSupplierName && aiSupplierName.toLowerCase() !== String(selSupplier?.legal_name ?? "").toLowerCase()) {
        try {
          const { error: aliasErr } = await supabase.schema("app").rpc("record_supplier_alias", {
            p_source_name: aiSupplierName,
            p_supplier_id: supplierId,
            p_abn: aiSupplierAbn || null,
          });
          if (aliasErr) console.warn("record_supplier_alias:", aliasErr.message);
        } catch (e: any) { console.warn("record_supplier_alias:", e?.message ?? e); }
      }
      // T-489 — the lines ARE the accounting detail, not a nicety, so unlike the
      // two memory calls above this one is never swallowed. Always called: an
      // empty array clears whatever a previous approval left on the invoice.
      if (apId) {
        const { error: linesErr } = await supabase.schema("app").rpc("save_ap_invoice_lines", {
          p_ap_invoice_id: apId,
          p_lines: lines.map((l) => ({
            description: l.description.trim(),
            quantity: l.quantity.trim(),
            unit_price: l.unit_price.trim(),
            amount_ex_gst: String(Number(l.amount_ex_gst) || 0),
            gst_amount: String(Number(l.gst_amount) || 0),
            xero_account_code: l.xero_account_code || xeroAccountCode,
            xero_tax_type: l.xero_tax_type || xeroTaxType,
            budget_code: budgetCode,
            project_id: projectId || "",
            is_collapsed: String(l.is_collapsed),
            collapsed_count: l.is_collapsed ? String(l.collapsed_count) : "",
          })),
        });
        if (linesErr) throw new Error(linesErr.message);
      }
      // mig 351 — a CapEx tool/equipment buy at/over the insurance threshold
      // must not vanish once coded: offer the asset register before closing.
      {
        const rule = assetRule ?? { threshold: 5000, exclude: ["CX01_VEHICLE"] };
        const codeU = (budgetCode || "").toUpperCase();
        const isCapexBuy = invoiceType === "capex" || codeU.startsWith("CX");
        // 8 Oct 2026 (mig 429) — every CapEx tool/equipment buy is offered to the
        // asset list, not just the ones over the insurance threshold. James:
        // "add to these lists from AP as that's the input flow, even just the
        // line and we back fill information."
        if (isCapexBuy && !rule.exclude.includes(codeU)) {
          setAssetPrompt({ value: totalNum, apId });
          return; // onDone fires when the asset prompt closes
        }
      }
      onDone();
    } catch (e: any) {
      setErr(e?.message ?? "Couldn't approve");
    } finally { setBusy(false); }
  }

  const showFtc = /FUEL/i.test(budgetCode) || manualFuel;

  return (
    <div className="grid grid-cols-[120px_1fr] gap-4 p-4 items-start">
      {assetPrompt && (
        <AssetRegisterPrompt
          value={assetPrompt.value}
          apId={assetPrompt.apId}
          threshold={(assetRule ?? { threshold: 5000 }).threshold}
          defaults={{
            description: description.trim() || [vendorName.trim(), "equipment purchase"].filter(Boolean).join(" — "),
            supplier: selSupplier?.legal_name ?? vendorName.trim(),
            purchaseDate: invoiceDate || new Date().toISOString().slice(0, 10),
            receiptRef: invoiceNumber || "",
            budgetCode,
          }}
          userId={(user as any)?.user_id ?? null}
          onClose={() => { setAssetPrompt(null); onDone(); }}
        />
      )}
      {/* receipt thumb */}
      <div>
        {imageUrl ? (
          isPdf
            ? <button type="button" onClick={openImage} title="Open PDF" className="aspect-[3/4] w-full bg-ink border border-line rounded-md flex flex-col items-center justify-center text-text-muted hover:border-accent"><FileText size={28} /><span className="text-[10px] mt-1">PDF</span></button>
            : <img src={imageUrl} alt="Receipt" title="Click to open full size" onClick={openImage} style={{ cursor: "zoom-in" }} className="w-full rounded-md border border-line object-cover max-h-44 hover:border-accent" />
        ) : (
          <div className="aspect-[3/4] w-full bg-ink border border-dashed border-line rounded-md flex items-center justify-center text-text-muted"><Upload size={22} /></div>
        )}
      </div>

      {/* fields */}
      <div className="space-y-4 min-w-0">
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">What it is</div>
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0 relative">
              <input value={supplierId ? (selSupplier?.legal_name ?? "") : (vendorName || search)}
                onChange={(e) => { setSupplierId(""); setVendorName(e.target.value); setSearch(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)} placeholder="Supplier or one-off vendor"
                className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text" />
              {open && search.trim() && filteredSuppliers.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-ink-card border border-line rounded-md shadow-lg max-h-44 overflow-y-auto">
                  {filteredSuppliers.map((s) => (
                    <button key={s.supplier_id} type="button" onClick={() => { setSupplierId(s.supplier_id); setVendorName(""); setSearch(""); setOpen(false); }}
                      className="w-full text-left px-3 py-1.5 hover:bg-ink-raised text-[12px]">{s.legal_name}</button>
                  ))}
                </div>
              )}
            </div>
            {/* T-489 — the total printed on the DOCUMENT, never the sum of our
                lines. Letting the lines rewrite it made a $533.78 invoice present
                as $484.55, which anyone could have approved without expanding it.
                The lines are an allocation of this figure and must add up to it. */}
            <div className="shrink-0">
              <input value={total} onChange={(e) => splitTotal(e.target.value)} placeholder="0.00"
                title="The total printed on the invoice"
                className="w-24 bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text text-right" />
              {hasLines && (
                <div className={`mt-0.5 text-right text-[10px] ${lineVariance == null ? "text-ok" : "text-warn"}`}>
                  {lineVariance == null ? "lines agree" : `lines out by ${money(Math.abs(lineVariance))} — posts as one line`}
                </div>
              )}
            </div>
          </div>

          {/* T-487 — say how the supplier got picked, and leave an easy way out.
              A wrong silent auto-match is worse than no match at all. */}
          {supplierId && resolvedVia && (
            <div className="flex items-center gap-2 flex-wrap text-[11px] text-text-muted">
              <span>
                {resolvedVia === "abn" ? "Matched on ABN"
                  : resolvedVia === "alias" ? "Matched from a name you linked before"
                  : "Matched on name"}
                {aiSupplierName ? ` — invoice says "${aiSupplierName}"` : ""}
              </span>
              <button type="button" onClick={unresolveSupplier} className="underline hover:text-text">not this one</button>
            </div>
          )}

          {/* Fuzzy candidates: offered as chips, picked by hand. Hidden while they
              are typing their own search so this never competes with the dropdown. */}
          {!supplierId && !search.trim() && fuzzySuppliers.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] text-text-muted">Close match?</span>
              {fuzzySuppliers.map((s) => (
                <button key={s.supplier_id} type="button" onClick={() => pickFuzzySupplier(s)}
                  className="px-2 py-1 rounded border border-line bg-ink hover:bg-ink-raised text-[11px] text-text">
                  {s.legal_name} <span className="text-text-muted">{Math.round(Number(s.confidence ?? 0) * 100)}%</span>
                </button>
              ))}
            </div>
          )}

          {resolveDone && !supplierId && fuzzySuppliers.length === 0 && vendorName.trim() && (
            <div className="text-[11px] text-text-muted">No supplier matched — saves as a one-off vendor unless you pick or add one.</div>
          )}

          {/* Quick-add supplier — one-off vendors stay name-only by design, but a
              real recurring supplier (plant hire etc) gets promoted right here. */}
          {!supplierId && vendorName.trim().length > 2 && (
            !addingSupplier ? (
              <button type="button" onClick={() => setAddingSupplier(true)} className="text-[11px] text-accent hover:underline">
                + Add "{vendorName.trim()}" to the supplier register (recurring suppliers only — one-offs can stay as-is)
              </button>
            ) : (
              <div className="flex items-end gap-2 flex-wrap border border-line rounded-md p-2.5 bg-ink-raised">
                <label className="text-[10px] text-text-muted">ABN
                  <input value={nsAbn} onChange={(e) => setNsAbn(e.target.value)} placeholder="optional"
                    className="mt-0.5 block w-36 bg-ink border border-line rounded px-2 py-1 text-[12px] text-text" />
                </label>
                <label className="text-[10px] text-text-muted">Billing email
                  <input value={nsEmail} onChange={(e) => setNsEmail(e.target.value)} placeholder="optional"
                    className="mt-0.5 block w-48 bg-ink border border-line rounded px-2 py-1 text-[12px] text-text" />
                </label>
                <Button size="sm" disabled={nsSaving} onClick={quickAddSupplier}>{nsSaving ? "Adding…" : `Add ${vendorName.trim().slice(0, 18)}`}</Button>
                <Button size="sm" variant="secondary" onClick={() => setAddingSupplier(false)}>Cancel</Button>
              </div>
            )
          )}

          <input value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Description for Xero — e.g. 63.57L diesel, plant"
            className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[12px] text-text" />
        </div>

        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Coding</div>

          {isStatement && xeroEntryType === "spend_money" && selCard && !showSplitAnyway ? (
            // 23 Sep 2026 — a statement PAID ON A COMPANY CARD is one spend money
            // with its own lines. Say so quietly; keep the splitter one click away.
            <div className="border border-line rounded px-2.5 py-2 text-[11.5px] text-text-muted flex items-center gap-2 flex-wrap">
              <span>
                <span className="font-medium text-text">Statement, paid on card</span> — approving it as one
                spend money is the clean way. GST is never taken from the balance, only from what the document states.
              </span>
              <button type="button" onClick={() => setShowSplitAnyway(true)}
                      className="ml-auto text-[11px] text-accent hover:underline">
                Split into records instead
              </button>
            </div>
          ) : isStatement && (
            <div className="border border-warn/50 bg-warn/10 rounded px-2.5 py-2 text-[11.5px] text-text">
              <span className="font-medium">Account statement.</span>
              <div className="text-text-muted mt-1">
                The amount is a closing balance, and it only equals a real invoice when last
                period was paid in full. Best: split it below — one record per invoice, each
                with its own number, due date and total. You can still approve it as one
                record if that's how you want to track it; the totals reconcile in Xero, and
                GST is never taken from the balance — only from what the document states.
              </div>
              {kindNote && <div className="text-text-dim mt-1 italic">{kindNote}</div>}
              <StatementSplitPanel receipt={receipt} ai={ai} onDone={onDone} />
            </div>
          )}

          {/* BK-312 1.3 / 4.4 — the amount has no tax invoice behind it (split
              from a statement line, or a private sale). Approve is blocked in
              approve(); this explains why and offers the way out. */}
          {receipt?.no_tax_invoice && (
            <div className="border border-warn/50 bg-warn/10 rounded px-2.5 py-2 text-[11.5px] text-text">
              <span className="font-medium">No tax invoice on file for this amount.</span>
              <div className="text-text-muted mt-1">
                It came off a statement line or a private sale. GST can't be claimed and it
                can't be approved until the underlying tax invoice (or the evidence pack, for
                a private sale) is attached to this record's paperwork.
              </div>
              <button type="button"
                onClick={async () => {
                  if (!window.confirm("Confirm the tax invoice / evidence is now attached to this record's file? This unblocks approval.")) return;
                  await supabase.from("pending_receipts").update({ no_tax_invoice: false } as any).eq("receipt_id", receipt.receipt_id);
                  onDone();
                }}
                className="mt-1.5 text-[11px] text-accent hover:underline">
                The invoice has arrived and is attached — unblock this record
              </button>
            </div>
          )}

          {/* The model returned an account that is not in the chart, so it was
              dropped. Saying so turns an unexplained empty box into a known
              one-dropdown job. */}
          {accountFlags.length > 0 && (
            <div className="border border-warn/40 bg-warn/8 rounded px-2.5 py-2 text-[11px] text-text">
              {accountFlags.map((n, i) => <div key={i}>{n}</div>)}
            </div>
          )}

          {poMatch && (
            <div className="text-[11.5px] text-ok inline-flex items-center gap-1.5">
              <span className="font-mono">{poMatch.po_number}</span> is printed on this invoice — matched to our register{poMatch.po_title ? ` (${poMatch.po_title})` : ""}; job, PO and bucket set from it.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <select value={invoiceType} onChange={(e) => { setInvoiceType(e.target.value as any); setBudgetCode(""); }} className="bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text">
              <option value="project">Project</option><option value="opex">Opex</option><option value="capex">Capex</option>
            </select>
            {invoiceType === "project"
              ? <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text"><option value="">Project…</option>{lookups.projects.map((p) => <option key={p.project_id} value={p.project_id}>{[p.project_code, p.project_name, p.site?.site_name].filter(Boolean).join(" · ")}</option>)}</select>
              : <div />}
            <div className="col-span-2 md:col-span-3">
              {/* 28 Oct 2026 — the PO and the bucket, with what is left on each (replaces the bare code list and the PO nudge below) */}
              <BudgetPoPanel
                projectId={invoiceType === "project" ? projectId : null}
                supplierId={supplierId}
                invoiceType={invoiceType}
                codes={lookups.budgetCodes as any}
                budgetCode={budgetCode} onBudgetCode={setBudgetCode}
                poId={poId} onPo={setPoId}
                amountEx={Number(exGst) || 0}
                compact
              />
            </div>
          </div>

          {/* T-485 — the Xero GL account this posts to. Prefilled from how this
              supplier + budget code was coded last time; correcting it teaches it.
              Same picker shape as the supplier box above, so the two feel alike.
              It opens on an empty box, so a delayed blur is what closes it. */}
          <div>
            <XeroAccountPicker value={xeroAccountCode} onChange={pickXeroAccount}
              accounts={xeroAccounts} preferCode={suggested.current?.account} />
            {(codingHint ?? seedHint) && (
              <div className="text-[11px] text-text-muted mt-1">{codingHint ?? seedHint}</div>
            )}
          </div>

          {/* Mig 397 — which vehicle (only when this is a vehicle bill) */}
          {vehicleRule && (vehicleRule.accounts.includes(xeroAccountCode) || /^(CX01_VEHICLE|CX04_VEH_FITOUT|OX12_FUEL)$/.test(budgetCode)) && (
            <label className="flex items-center gap-2 text-[11px] text-text-muted">
              <span className="w-[76px] shrink-0">Vehicle</span>
              <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}
                className={`bg-ink border rounded px-2 py-1 text-[11px] text-text flex-1 ${vehicleId ? "border-line" : "border-accent/50"}`}>
                <option value="">Fleet — not one vehicle</option>
                {vehicleRule.vehicles.map((v) => (
                  <option key={v.vehicle_id} value={v.vehicle_id}>{[v.vehicle_code, v.rego, [v.make, v.model].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}</option>
                ))}
              </select>
              <span className="text-text-dim">so Commercial › Vehicles can cost it per ute</span>
            </label>
          )}

          {/* Mig 406 — litres, when it is a fuel bill. Read off the description; type over it. */}
          {(xeroAccountCode === "6200" || budgetCode === "OX12_FUEL") && (
            <label className="flex items-center gap-2 text-[11px] text-text-muted">
              <span className="w-[76px] shrink-0">Litres</span>
              <input type="number" step="0.01" min="0" value={fuelLitres}
                onChange={(e) => setFuelLitres(e.target.value)}
                onFocus={() => { if (!fuelLitres) setFuelLitres(litresFromText(description)); }}
                placeholder={litresFromText(description) || "blank for a monthly statement"}
                className="bg-ink border border-line rounded px-2 py-1 text-[11px] text-text w-32 font-mono" />
              {!fuelLitres && litresFromText(description) && (
                <button type="button" onClick={() => setFuelLitres(litresFromText(description))} className="text-accent hover:underline">use {litresFromText(description)} L from the description</button>
              )}
              <span className="text-text-dim">so the fuel table counts litres for a ute that isn't on the card</span>
            </label>
          )}

          {/* Mig 403 — which policy (only when this is an insurance bill) */}
          {insuranceRule && (insuranceRule.accounts.includes(xeroAccountCode) || budgetCode === "OX06_INS" || !!policyItemId) && (
            <label className="flex items-center gap-2 text-[11px] text-text-muted">
              <span className="w-[76px] shrink-0">Policy</span>
              <select value={policyItemId} onChange={(e) => setPolicyItemId(e.target.value)}
                className={`bg-ink border rounded px-2 py-1 text-[11px] text-text flex-1 ${policyItemId ? "border-line" : "border-accent/50"}`}>
                <option value="">Not one policy</option>
                {insuranceRule.policies.map((p) => (
                  <option key={p.item_id} value={p.item_id}>{[p.display_name, p.provider, p.reference].filter(Boolean).join(" · ")}{p.expiry_date ? ` (to ${p.expiry_date})` : ""}</option>
                ))}
              </select>
              <span className="text-text-dim">so Commercial › Insurance can cost it per policy</span>
            </label>
          )}

          {/* Mig 403 — settled by the bank, or by a loan (Elantis pays the
              insurer, NAB pays the dealer). Shown whenever a policy or a
              vehicle is in play, and always when it is already 'loan'. */}
          {insuranceRule && (settledBy === "loan" || !!policyItemId || budgetCode === "OX06_INS" || (vehicleRule && vehicleRule.accounts.includes(xeroAccountCode))) && (
            <div className="flex items-center gap-2 text-[11px] text-text-muted flex-wrap">
              <span className="w-[76px] shrink-0">Settled by</span>
              <div className="inline-flex rounded border border-line overflow-hidden">
                <button type="button" onClick={() => { setSettledBy("bank"); setSettledLoanId(""); }}
                  className={`px-2 py-1 ${settledBy === "bank" ? "bg-accent/15 text-accent" : "text-text-muted hover:text-text"}`}>Bank</button>
                <button type="button" onClick={() => setSettledBy("loan")}
                  className={`px-2 py-1 border-l border-line ${settledBy === "loan" ? "bg-accent/15 text-accent" : "text-text-muted hover:text-text"}`}>A loan pays it</button>
              </div>
              {settledBy === "loan" && (
                <select value={settledLoanId} onChange={(e) => setSettledLoanId(e.target.value)}
                  className={`bg-ink border rounded px-2 py-1 text-[11px] text-text flex-1 min-w-[200px] ${settledLoanId ? "border-line" : "border-warn/60"}`}>
                  <option value="">Which loan? (set it up on Commercial › Loans if it isn't here)</option>
                  {insuranceRule.loans.map((l) => (
                    <option key={l.loan_id} value={l.loan_id}>{l.lender} — {l.title}{l.status !== "active" ? ` (${l.status})` : ""}</option>
                  ))}
                </select>
              )}
              <span className="text-text-dim">{settledBy === "loan" ? "nothing leaves the bank for this bill — the loan instalments do" : "paid like any other bill"}</span>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap text-[11px]">
            {showFtc ? (
              <select value={ftcClass} onChange={(e) => setFtcClass(e.target.value)} className="bg-ink border border-line rounded px-2 py-1 text-[11px] text-text">
                <option value="">FTC…</option>
                <option value="FTC_OFFROAD_Plant">Plant / off-road</option>
                <option value="FTC_HV_OnRoad">Heavy on-road</option>
                <option value="FTC0_LightVehicle_OnRoad">Light vehicle</option>
                <option value="FTC_TANK_MIXED">Bulk / mixed</option>
              </select>
            ) : (
              <button type="button" onClick={() => { setManualFuel(true); if (!budgetCode) setBudgetCode("OX12_FUEL"); }} className="text-accent hover:underline">Mark fuel</button>
            )}
            <select value={brandId} onChange={(e) => applyBrand(e.target.value)} className="bg-ink border border-line rounded px-2 py-1 text-[11px] text-text">
              <option value="">{auto.brand ? `${auto.brand} (auto)` : "Brand…"}</option>
              {lookups.brands.map((b) => <option key={b.brand_id} value={b.brand_id}>{b.brand_display_name}</option>)}
            </select>
            <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-text-muted">
              Tax
              <span className="inline-block w-44">
                <Select value={xeroTaxType} onChange={(e) => pickTaxType(e.target.value)}
                  className={`text-[11px] ${!xeroTaxType ? "border-warn/60" : ""}`}>
                  {/* BK-312 2.4(c) — undecided is a real state that blocks
                      approval, not a silent default. */}
                  <option value="">Tax — you decide…</option>
                  {TAX_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </Select>
              </span>
            </label>
            {/* BK-312 2.1 — the GST as PRINTED, authoritative when present.
                Pre-filled from the read; typing corrects the read; clearing it
                falls back to the 1/11 derivation for docs with no GST line. */}
            {!taxIsFree && (
              <label className="inline-flex items-center gap-1 text-[11px] text-text-muted">
                GST as printed
                <input value={gstDraft ?? (gstFromDoc ? gst : "")}
                  inputMode="decimal"
                  onFocus={() => setGstDraft(gstFromDoc ? gst : "")}
                  onChange={(e) => setGstDraft(e.target.value.replace(/[^0-9.]/g, ""))}
                  onBlur={() => { if (gstDraft != null) { editStatedGst(gstDraft); setGstDraft(null); } }}
                  onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                  placeholder="none shown"
                  className="w-24 bg-ink border border-line rounded px-1.5 py-1 text-[11px] text-text text-right tabular-nums" />
              </label>
            )}
            {/* T-502 — always the HEADER figures, because those are what post.
                This used to switch to the line sums whenever lines existed, so a
                set of unpriced lines reported "ex 0.00 · GST 0.00" on an invoice
                with a real total. Line sums have their own readout below. */}
            <span className="text-text-muted">ex {headerEx} · GST {headerGst}</span>
          </div>

          {/* T-489 — the line split. Absent entirely unless the receipt actually
              has one: the ordinary single-amount docket must not grow a table it
              doesn't need. Collapsed when it is there, too — reading the split is
              a deliberate act, approving on the summary is the normal one. */}
          {/* No lines on the document. Offer the way in, quietly - one link, no
              table. Absent by default is right; unavailable was not. */}
          {!hasLines && (
            <button type="button" onClick={startSplit}
              className="text-[11px] text-accent hover:underline self-start">
              + Split into lines — to add a fee, or code part of it differently
            </button>
          )}

          {/* T-AP-GST-GUARD — amber, never blocking: the push still lands as a
              Draft, but the coder sees the mismatch BEFORE it goes anywhere. */}
          {xeroGuard != null && (
            <div className="rounded-md border border-warn/50 bg-warn/10 px-3 py-2 text-[12px] text-text space-y-1">
              <div className="flex items-center gap-1.5 text-warn font-medium">
                <AlertTriangle size={13} /> Xero will show GST {money(xeroGuard.predicted)} — the document says {money(xeroGuard.stated)}
              </div>
              <div className="text-text-muted">
                A single {taxIsFree ? "tax-free" : "GST"} line can't reproduce this invoice. Usual cause:
                stamp duty or levies inside the total — those are BAS Excluded (insurance bills are
                premium + fees with GST, stamp duty without — never one line).
              </div>
              {xeroGuard.basSplit != null && (
                <button type="button" onClick={() => applyBasSplit(xeroGuard.basSplit!)}
                  className="text-accent hover:underline text-[12px]">
                  Split {money(xeroGuard.basSplit)} out as a BAS Excluded line (stamp duty / levies)
                </button>
              )}
            </div>
          )}

          {hasLines && (
            <div className="border border-line rounded-md bg-ink-raised">
              <button type="button" onClick={() => setLinesOpen((v) => !v)}
                className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] text-text">
                {linesOpen ? <ChevronDown size={12} className="text-text-muted" /> : <ChevronRight size={12} className="text-text-muted" />}
                Lines · {lines.length} · <span className="text-[13px] text-text font-medium">{money(linesTotal)}</span>
                {/* T-489 — a clean reconcile is worth saying out loud. Silence is
                    ambiguous when you are about to approve money. */}
                {lineVariance == null && gstCheck == null && Number(total) > 0 && (
                  <span className="ml-auto text-[11px] text-ok">✓ matches the invoice</span>
                )}
                {(lineVariance != null || gstCheck != null) && (
                  <span className="ml-auto text-[11px] text-warn text-right">
                    {lineVariance != null && <>invoice is {money(Number(total) || 0)} · lines out by {money(Math.abs(lineVariance))} · the total and GST still post correctly</>}
                    {lineVariance != null && gstCheck != null && <br />}
                    {gstCheck != null && <>GST on the invoice is {money(gstCheck.stated)} · lines derive {money(linesGst)}</>}
                  </span>
                )}
              </button>
              {linesOpen && (
                <div className="border-t border-line p-2 space-y-2">
                  {/* G1 — how to read the printed amounts. Sits at the top of the
                      split because it changes the meaning of every line below it. */}
                  <div className="flex items-center gap-2 text-[11px] text-text-muted">
                    <span>Line amounts on this document are</span>
                    <div className="inline-flex rounded border border-line overflow-hidden">
                      <button type="button" onClick={() => setPrintedMode("inc")}
                        className={`px-2 py-0.5 text-[11px] transition-colors ${
                          printedAs === "inc" ? "bg-accent/15 text-accent" : "text-text-muted hover:text-text"}`}>
                        GST inclusive
                      </button>
                      <button type="button" onClick={() => setPrintedMode("ex")}
                        className={`px-2 py-0.5 text-[11px] transition-colors border-l border-line ${
                          printedAs === "ex" ? "bg-accent/15 text-accent" : "text-text-muted hover:text-text"}`}>
                        GST exclusive
                      </button>
                    </div>
                    <span className="text-text-dim">
                      {printedAs === "inc"
                        ? "receipts and dockets normally print inclusive"
                        : "trade invoices often print ex-GST and add GST at the bottom"}
                    </span>
                  </div>
                  {lines.map((l, i) => (
                    <div key={l.key} className="rounded border border-line/60 bg-ink/40 p-2 space-y-1.5">
                      {/* What it is, on its own row — this is the line you actually
                          read when checking an invoice against the paperwork. */}
                      <div className="flex items-center gap-1.5">
                        {i === 0 && <span className="sr-only">Description</span>}
                        <input value={l.description} onChange={(e) => patchLine(l.key, { description: e.target.value })}
                          placeholder="Description"
                          className="flex-1 min-w-0 bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text" />
                        {/* Say so when a row stands for several off the receipt, or the
                            count in Xero won't reconcile against the paperwork. */}
                        {l.is_collapsed && (
                          <span title={`Summarises ${l.collapsed_count} small lines off the receipt`}
                            className="shrink-0 px-1.5 py-0.5 rounded border border-line text-[10px] text-text-muted">×{l.collapsed_count}</span>
                        )}
                        <button type="button" title="Remove this line" onClick={() => removeLine(l.key)}
                          className="shrink-0 px-1.5 text-[13px] text-text-muted hover:text-danger">×</button>
                      </div>
                      {/* The numbers and the account, labelled. Labels repeat per row
                          rather than sitting in a header, because the rows are tall
                          enough that a single header scrolls out of sight. */}
                      <div className="flex items-end gap-1.5">
                        <label className="shrink-0 text-[10px] text-text-muted">Qty
                          <input value={l.quantity} onChange={(e) => patchLine(l.key, { quantity: e.target.value })}
                            className="block w-14 bg-ink border border-line rounded px-1.5 py-1 text-[11px] text-text text-right" />
                        </label>
                        <label className="shrink-0 text-[10px] text-text-muted">Unit
                          <input value={l.unit_price} onChange={(e) => patchLine(l.key, { unit_price: e.target.value })}
                            className="block w-16 bg-ink border border-line rounded px-1.5 py-1 text-[11px] text-text text-right" />
                        </label>
                        <label className="shrink-0 text-[10px] text-text-muted">Amount (as printed{printedAs === "ex" ? ", ex GST" : ""})
                          <input value={l.amount_inc} onChange={(e) => patchLine(l.key, { amount_inc: e.target.value })}
                            placeholder="0.00"
                            className="block w-24 bg-ink border border-line rounded px-1.5 py-1 text-[12px] text-text text-right" />
                        </label>
                        {/* BK-312 / James, 24 Aug: "we can only toggle GST on
                            the total, not individual lines — but one line can be
                            a GST-free rego or a bank fee on a 10% invoice." Each
                            line owns its treatment; changing it re-derives the
                            line's ex/GST on the spot. */}
                        <label className="shrink-0 text-[10px] text-text-muted">Tax
                          <select value={l.xero_tax_type || xeroTaxType || ""}
                            onChange={(e) => patchLine(l.key, { xero_tax_type: e.target.value })}
                            className="block bg-ink border border-line rounded px-1 py-1 text-[10px] text-text w-[92px]">
                            <option value="">Tax…</option>
                            <option value="GST on Expenses">GST 10%</option>
                            <option value="GST Free Expenses">GST Free</option>
                            <option value="BAS Excluded">BAS Excl.</option>
                          </select>
                        </label>
                        <div className="shrink-0 text-[10px] text-text-muted">
                          <div>ex / GST</div>
                          <div className="py-1 text-[11px] text-text-muted tabular-nums">
                            {Number(l.amount_ex_gst || 0).toFixed(2)} · {Number(l.gst_amount || 0).toFixed(2)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0 text-[10px] text-text-muted">Xero account
                          <XeroAccountPicker compact value={l.xero_account_code} accounts={xeroAccounts}
                            onChange={(code) => pickLineAccount(l.key, code)} preferCode={xeroAccountCode} />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-0.5">
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={addLine} className="text-[11px] text-accent hover:underline">+ Add line</button>
                      {/* T-489 — chasing a missing line item around a docket is not
                          what the money cares about. The account, the GST and the
                          total are. So book the gap as its own line, coded and
                          taxed like the rest, and let the description carry the
                          fact that it needs eyes. */}
                      {lineVariance != null && (
                        <button type="button"
                          onClick={() => {
                            const gap = -lineVariance;   // lines are short by this much
                            setLines((prev) => [...prev, blankLine({
                              description: "Unallocated balance — check against receipt",
                              amount_inc: gap.toFixed(2),
                              xero_account_code: xeroAccountCode,
                              xero_tax_type: taxForAccount(xeroAccountCode),
                              ...splitInc(gap.toFixed(2), taxForAccount(xeroAccountCode)),
                            })]);
                          }}
                          className="text-[11px] text-warn hover:underline">
                          Balance to {money(Number(total) || 0)}
                        </button>
                      )}
                    </div>
                    <div className="text-[11px] text-text-muted">
                      {lines.length} lines · ex {money(linesEx)} · GST {money(linesGst)} ·{" "}
                      <span className="text-[15px] text-text font-semibold align-middle">{money(linesTotal)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Payment</div>
          {/* T-442 — Bill vs Spend-money, set here so the Xero entry is hinted from the
              start (auto from the due date / payment routing; tap to override).
              The card sits on the same row because the card pick is what decides it. */}
          <div className="flex items-center gap-2 flex-wrap">
            <select value={cardId} onChange={(e) => setCardId(e.target.value)} className="bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text">
              <option value="">No card / bill</option>
              {lookups.cards.map((c) => (
                <option key={c.card_id} value={c.card_id}>
                  ••••{c.last4}{c.label ? ` · ${c.label}` : ""}{c.card_type === "employee_personal" ? " — PERSONAL (reimburse)" : ""}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2 text-[11px] flex-wrap">
              <span className="text-text-muted">Xero entry:</span>
              <div className="inline-flex rounded border border-line overflow-hidden">
                <button type="button" onClick={() => setEntryOverride("bill")}
                  className={`px-2.5 py-1 ${xeroEntryType === "bill" ? "bg-accent text-white" : "text-text-muted"}`}>Bill · to pay</button>
                {/* A reimbursement is never "spend money" - the company hasn't
                    paid anything yet; the person has, and the bundle turns it
                    into a bill to them. A manual spend-money override here is
                    how Josh's Uber receipt vanished from Bills to pay while
                    sitting inside a bundled bill (26 Aug). So the option simply
                    isn't offered on a reimbursement. */}
                {!cardIsReimb && (
                  <button type="button" onClick={() => setEntryOverride("spend_money")}
                    className={`px-2.5 py-1 ${xeroEntryType === "spend_money" ? "bg-accent text-white" : "text-text-muted"}`}>Spend money · paid</button>
                )}
              </div>
              <span className="text-text-dim">
                {cardIsReimb ? "reimbursement — always a bill to the person" : entryOverride ? "manual" : selCard ? "auto: paid on card" : dueDate ? "auto: has due date" : "auto: no due date"}
              </span>
            </div>
          </div>

          {/* Dates — from the AI read; editable. Important for Xero + due tracking. */}
          <div className="grid grid-cols-3 gap-2">
            <label className="text-[10px] text-text-muted">Invoice date
              <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="mt-0.5 w-full bg-ink border border-line rounded px-2 py-1 text-[12px] text-text" />
            </label>
            <label className="text-[10px] text-text-muted">Due date
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-0.5 w-full bg-ink border border-line rounded px-2 py-1 text-[12px] text-text" />
              {/* 23 Sep 2026 — the document said something that isn't a date
                  ("Due Now"). Show it; leave the field blank; a blank due date
                  routes to Spend money, which is what a paid statement is. */}
              {!dueDate && dateTextOf(ai, "due_date") && (
                <span className="block text-[10px] text-warn mt-0.5">Document says “{dateTextOf(ai, "due_date")}” — not a date, left blank</span>
              )}
            </label>
            <label className="text-[10px] text-text-muted">Invoice #
              <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="—" className="mt-0.5 w-full bg-ink border border-line rounded px-2 py-1 text-[12px] text-text" />
            </label>
          </div>

          {/* T-380 — PO link nudge: if this invoice fulfils a PO, linking it here
              nets the PO's committed spend down so the project never double-counts. */}
          {openPos.length > 0 && invoiceType !== "project" && (
            <div className="flex items-center gap-2 flex-wrap">
              <select value={poId} onChange={(e) => setPoId(e.target.value)}
                className="bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text">
                <option value="">No PO / not against a PO</option>
                {candidatePos.map((p: any) => (
                  <option key={p.po_id} value={p.po_id}>
                    {p.po_number} · {p.po_title ?? ""} · {money(Number(p.total_inc_gst ?? 0))}
                  </option>
                ))}
              </select>
              {supplierId && candidatePos.some((p: any) => p.supplier_id === supplierId) && !poId && (
                <span className="text-[11px] text-warn">This supplier has open PO(s) — link if this invoice is against one.</span>
              )}
            </div>
          )}

          {/* Routing banner — ALWAYS visible so reimbursement vs company spend is
              never a surprise. Driven by the worker's capture declaration and/or
              the card pick. */}
          {cardIsReimb ? (
            <div className="px-3 py-2 rounded-md bg-warn/15 border border-warn/50 text-[12px] text-warn font-medium">
              REIMBURSEMENT — {claimedPersonal && !selCard
                ? "the worker declared they paid PERSONALLY at capture: this becomes a bill paying them back"
                : `personal card${selCard?.label ? ` (${selCard.label})` : ""}: this becomes a bill that pays the cardholder back, not company spend`}.
            </div>
          ) : selCard ? (
            <div className="px-3 py-2 rounded-md bg-ok/10 border border-ok/40 text-[12px] text-ok">
              Company card ••••{selCard.last4} — spend money → {selCard.xero_account_name ?? "card account"}. No repayment due.
              {claimedPersonal && (
                <div className="text-warn mt-1 font-medium">
                  ⚠ The worker declared PERSONAL payment at capture, but a company card is picked — double-check before approving.
                </div>
              )}
            </div>
          ) : (
            <div className="px-3 py-2 rounded-md bg-ink-raised border border-line text-[11px] text-text-muted">
              No card picked — routes as a supplier bill (EFT). If it was paid on a card, pick it above (a personal card flags it as a reimbursement).
            </div>
          )}
        </div>

        {err && <div className="text-[11px] text-danger"><AlertTriangle size={10} className="inline mr-1" />{err}</div>}

        <div className="flex justify-end gap-2">
          {receipt && <Button size="sm" variant="secondary" icon={Ban} onClick={reject} disabled={busy}>Reject</Button>}
          <Button size="sm" icon={CheckCircle2} onClick={approve} disabled={busy}>{busy ? "…" : "Approve"}</Button>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// AssetRegisterPrompt — mig 351. Shown right after approving a CapEx item at
// or over the insurance threshold. "Add to asset register" books it with
// insurance_status = pending (visibly outstanding until confirmed); Skip
// just closes. Broker email stays manual for v1 — the point is capture.
//
// 8 Oct 2026 (mig 429) — now offered on every CapEx tool/equipment buy. Under
// the threshold it asks which list (IT, phone, test equipment, radio, other)
// and insurance starts un-ticked; no serial means the row is marked "details
// still to fill in" for the Assets screen.
// =============================================================================
function AssetRegisterPrompt({ value, apId, threshold, defaults, userId, onClose }: {
  value: number;
  apId: string | null;
  threshold: number;
  defaults: { description: string; supplier: string; purchaseDate: string; receiptRef: string; budgetCode: string };
  userId: string | null;
  onClose: () => void;
}) {
  const [desc, setDesc] = useState(defaults.description);
  const [serial, setSerial] = useState("");
  const [location, setLocation] = useState("");
  const over = value >= threshold;
  const [insReq, setInsReq] = useState(over);
  const [category, setCategory] = useState("other");
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  async function save() {
    if (!desc.trim()) { setSaveErr("Give the asset a description."); return; }
    setSaving(true); setSaveErr(null);
    const { error } = await (supabase as any).from("company_assets").insert({
      description: desc.trim(),
      purchase_date: defaults.purchaseDate || null,
      purchase_value: value,
      supplier_name: defaults.supplier || null,
      receipt_ref: defaults.receiptRef || null,
      ap_invoice_id: apId,
      budget_code: defaults.budgetCode || null,
      serial_number: serial.trim() || null,
      location: location.trim() || null,
      insurance_required: insReq,
      insurance_status: insReq ? "pending" : "not_required",
      category,
      // just the line for now — the Assets screen shows "Fill in" until someone does
      details_pending: !serial.trim(),
      created_by: userId,
    });
    setSaving(false);
    if (error) { setSaveErr(error.message); return; }
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-24 px-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={15} className="text-warn" />
            <div className="text-[14px] font-semibold text-text">{over ? `Significant asset — over $${threshold.toLocaleString()}` : "Add this to the asset list?"}</div>
          </div>
          <div className="text-[12px] text-text-muted mb-4">
            {over ? (
              <>Gear over ${threshold.toLocaleString()} isn't covered by the standard vehicle-contents insurance —
              it needs to be registered with the broker individually. Add it to the asset register so the
              insurance requirement can't disappear after coding.</>
            ) : (
              <>This was coded as a capital purchase. Add it to the asset list now with just this line —
              the serial, model and who has it can be filled in later on the Assets screen.</>
            )}
          </div>
          <div className="space-y-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">Which list</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="mt-1 w-full bg-ink-raised border border-line rounded-md px-3 py-2 text-[13px] text-text">
                <option value="it">IT (laptop, tablet, screen)</option>
                <option value="phone">Mobile phone</option>
                <option value="test_equipment">Test equipment</option>
                <option value="radio">Radio</option>
                <option value="other">Other equipment</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">Asset description</span>
              <input value={desc} onChange={(e) => setDesc(e.target.value)}
                className="mt-1 w-full bg-ink-raised border border-line rounded-md px-3 py-2 text-[13px] text-text" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">Serial no. (if any)</span>
                <input value={serial} onChange={(e) => setSerial(e.target.value)}
                  className="mt-1 w-full bg-ink-raised border border-line rounded-md px-3 py-2 text-[13px] text-text" />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">Vehicle / location</span>
                <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Hilux 402 · depot"
                  className="mt-1 w-full bg-ink-raised border border-line rounded-md px-3 py-2 text-[13px] text-text placeholder:text-text-dim" />
              </label>
            </div>
            <div className="text-[12px] text-text-muted">
              ${value.toLocaleString()} · {defaults.supplier || "supplier not set"} · {defaults.budgetCode}
              {defaults.receiptRef ? ` · inv ${defaults.receiptRef}` : ""}
            </div>
            <label className="flex items-center gap-2 text-[13px] text-text">
              <input type="checkbox" className="accent-accent" checked={insReq} onChange={(e) => setInsReq(e.target.checked)} />
              Needs individual insurance (stays outstanding until marked insured)
            </label>
          </div>
          {saveErr && <div className="text-[12px] text-danger mt-3">{saveErr}</div>}
          <div className="mt-4 flex items-center justify-between">
            <button type="button" onClick={onClose} className="text-[12px] text-text-muted hover:text-text">
              Skip — not an individual asset
            </button>
            <Button size="sm" icon={CheckCircle2} onClick={() => void save()} disabled={saving}>
              {saving ? "Adding…" : "Add to asset register"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// StatementSplitPanel — BK-312 §1. A statement never becomes one bill: it
// becomes one record PER UNDERLYING INVOICE, each carrying that invoice's own
// number, due date and total. Records whose amounts came off statement lines
// (no tax invoice in the document) are flagged no_tax_invoice: GST Free,
// Approve blocked until the real invoice is attached. The statement itself
// stays visible as the document of record under status 'split'.
//
// Regression (the HPG capture): the 25/08 PDF must yield 467/$6,616.41 and
// 448/$203.54 — never one record of $6,819.95, and never $620.00 of GST.
// =============================================================================
function StatementSplitPanel({ receipt, ai, onDone }: { receipt: any; ai: any; onDone: () => void }) {
  const [splitting, setSplitting] = useState(false);
  const [splitErr, setSplitErr] = useState<string | null>(null);
  const items: any[] = Array.isArray(ai.statement_invoices) ? ai.statement_invoices : [];

  async function runSplit() {
    if (!receipt) return;
    setSplitting(true); setSplitErr(null);
    try {
      // Owner over agent (BK-312 §6): the supplier on every child is the
      // entity that made the supply.
      const supplierName = ai.owner_name || ai.supplier_name || null;
      const supplierAbn  = ai.owner_abn  || ai.supplier_abn  || null;
      const rows = items.map((si) => {
        const hasInvoice = !!si.tax_invoice_present;
        const gstStated  = hasInvoice && si.gst_amount != null ? Number(si.gst_amount) : null;
        return {
          captured_by_employee_id: receipt.captured_by_employee_id,
          storage_path: receipt.storage_path,
          mime_type: receipt.mime_type ?? null,
          status: "new",
          no_tax_invoice: !hasInvoice,
          source_statement_receipt_id: receipt.receipt_id,
          supplier_guess: supplierName,
          amount_guess: Number(si.total_inc_gst) || null,
          notes: `Split from statement (invoice ${si.invoice_number})`,
          ocr_raw_json: {
            document_kind: "invoice",
            supplier_name: supplierName,
            supplier_abn: supplierAbn,
            ...(ai.agent_name ? { agent_name: ai.agent_name } : {}),
            invoice_number: String(si.invoice_number ?? ""),
            invoice_date: si.invoice_date ?? null,
            due_date: si.due_date ?? null,
            total_inc_gst: Number(si.total_inc_gst) || 0,
            // GST only when the full tax invoice is in this document —
            // a statement line supports no GST claim at all.
            ...(gstStated != null ? { gst_amount: gstStated } : {}),
            suggested_tax_type: hasInvoice
              ? (gstStated && gstStated > 0 ? "GST on Expenses" : "GST Free Expenses")
              : "GST Free Expenses",
            line_items: [],
            notes: `Split from a statement of account${si.description ? ` — ${si.description}` : ""}. ${hasInvoice ? "Full tax invoice present in the source document." : "NO TAX INVOICE in the source document — amount from the statement line."}`,
            confidence: ai.confidence ?? { overall: 0.9 },
          },
        };
      });
      const { error: insErr } = await supabase.from("pending_receipts").insert(rows as any);
      if (insErr) throw insErr;
      const { error: updErr } = await supabase.from("pending_receipts")
        .update({ status: "split" }).eq("receipt_id", receipt.receipt_id);
      if (updErr) throw updErr;
      onDone();
    } catch (e: any) {
      setSplitErr(e?.message ?? String(e));
    } finally { setSplitting(false); }
  }

  if (items.length === 0) {
    return (
      <div className="mt-2 text-[11px] text-text-muted">
        This read predates the statement splitter, so the invoice list wasn't captured.
        Re-run the AI read on this document to pick it up — or reject this record and load
        the underlying tax invoices individually.
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-1.5">
      <div className="rounded border border-line/70 overflow-hidden">
        {items.map((si, i) => (
          <div key={i} className="flex items-center gap-2 px-2 py-1 text-[11px] border-b border-line/40 last:border-b-0 bg-ink/40">
            <span className="font-mono text-text">#{si.invoice_number}</span>
            <span className="text-text font-medium tabular-nums">${Number(si.total_inc_gst || 0).toFixed(2)}</span>
            {si.due_date && <span className="text-text-muted">due {si.due_date}</span>}
            {si.tax_invoice_present
              ? <span className="ml-auto text-ok">tax invoice in this document{si.gst_amount != null ? ` · GST $${Number(si.gst_amount).toFixed(2)}` : ""}</span>
              : <span className="ml-auto text-warn">statement line only — GST Free, blocked until its invoice arrives</span>}
          </div>
        ))}
      </div>
      {splitErr && <div className="text-[11px] text-danger">{splitErr}</div>}
      <button type="button" onClick={runSplit} disabled={splitting}
        className="text-[12px] px-2.5 py-1 rounded border border-accent/50 bg-accent/10 text-accent hover:bg-accent/20 disabled:opacity-50">
        {splitting ? "Splitting…" : `Split into ${items.length} record${items.length === 1 ? "" : "s"} — one per invoice`}
      </button>
    </div>
  );
}
